import React, { useEffect, useState, useCallback, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { api, mediaUrl } from '../api';
import useIdleTimeout from '../hooks/useIdleTimeout';
import AdminConfirmModal from '../components/AdminConfirmModal';
import { downloadFoodMenuImage, groupFoodMenuForFlyer } from '../lib/menuFlyer';
import orderScheduleHint from '../lib/orderScheduleHint';
import './AdminPage.css';

const VENUE_LABELS = { kitchen: 'Kitchen', cafe: 'Holy Ground Cafe', desserts: 'Desserts', counter: 'Counter' };
const KITCHEN_LABELS = {
  st_noufer: "St Noufer's Kitchen",
  st_barbara: "St Barbara's Kitchen",
  turkish_coffee: 'Turkish Coffee',
};
const DAY_NAMES = ['sunday', 'monday', 'tuesday', 'wednesday', 'thursday', 'friday', 'saturday'];
const DAY_SHORT = { sunday: 'Sun', monday: 'Mon', tuesday: 'Tue', wednesday: 'Wed', thursday: 'Thu', friday: 'Fri', saturday: 'Sat' };

function digitsOnly(raw) {
  return String(raw ?? '').replace(/[^\d]/g, '');
}

function stockToInput(value) {
  return value === null || value === undefined ? '' : String(value);
}

// Stock box types locally and writes once on blur / Enter. A save on every
// digit used to reload the whole Admin catalog and fight the caret.
function SettingsSwitchRow({ title, description, checked, onChange }) {
  return (
    <div className="settings-row">
      <div className="settings-row-text">
        <div className="settings-row-title">{title}</div>
        {description ? <div className="settings-row-desc">{description}</div> : null}
      </div>
      <label className="settings-switch">
        <input type="checkbox" checked={checked} onChange={onChange} />
        <span className="settings-switch-track" aria-hidden="true" />
      </label>
    </div>
  );
}

function StockQtyInput({ value, onCommit }) {
  const [draft, setDraft] = useState(null);
  const saved = stockToInput(value);
  const shown = draft !== null ? draft : saved;

  const commit = async () => {
    const next = draft !== null ? draft : saved;
    if (next === saved) {
      setDraft(null);
      return;
    }
    try {
      await onCommit(next === '' ? null : Number(next));
      setDraft(null);
    } catch (err) {
      window.alert(err.message || 'Could not update stock');
    }
  };

  return (
    <input
      type="text"
      inputMode="numeric"
      autoComplete="off"
      className="stock-input"
      value={shown}
      placeholder="Unlimited"
      onChange={(e) => setDraft(digitsOnly(e.target.value))}
      onBlur={commit}
      onKeyDown={(e) => {
        if (e.key === 'Enter') e.currentTarget.blur();
      }}
      onClick={(e) => e.stopPropagation()}
    />
  );
}

// Report filter chips. Keys must match REPORT_GROUP_PREDICATES on the server
// (server/src/routes/admin.js) so the include/exclude wiring stays in sync.
const REPORT_GROUPS = [
  { key: 'noufer',      label: "St Noufer's Kitchen" },
  { key: 'barbara',     label: "St Barbara's Kitchen" },
  { key: 'turkish_coffee', label: 'Turkish Coffee' },
  { key: 'cafe_drinks', label: 'Cafe Drinks' },
  { key: 'cafe_food', label: 'Cafe Food' },
  { key: 'desserts', label: 'Desserts' },
];

/** SQLite local datetimes and ISO strings; avoids Safari issues with space-separated dates. */
function parseDbDateTime(value) {
  if (value == null || value === '') return null;
  if (typeof value === 'number') {
    const d = new Date(value);
    return Number.isNaN(d.getTime()) ? null : d;
  }
  const s = String(value).trim();
  if (!s) return null;
  const iso = /\d{4}-\d{2}-\d{2}T/.test(s) ? s : s.replace(/^(\d{4}-\d{2}-\d{2})[\sT]+(.*)$/, '$1T$2');
  const d = new Date(iso);
  return Number.isNaN(d.getTime()) ? null : d;
}

function fmtAU(d) {
  if (!d) return '—';
  const dd = String(d.getDate()).padStart(2, '0');
  const mm = String(d.getMonth() + 1).padStart(2, '0');
  const yyyy = d.getFullYear();
  const h = d.getHours() % 12 || 12;
  const min = String(d.getMinutes()).padStart(2, '0');
  const sec = String(d.getSeconds()).padStart(2, '0');
  const ampm = d.getHours() < 12 ? 'AM' : 'PM';
  return `${dd}/${mm}/${yyyy}, ${h}:${min}:${sec} ${ampm}`;
}

function fmtAUDate(d) {
  if (!d) return '—';
  const dd = String(d.getDate()).padStart(2, '0');
  const mm = String(d.getMonth() + 1).padStart(2, '0');
  const yyyy = d.getFullYear();
  return `${dd}/${mm}/${yyyy}`;
}

/** Compact SVG trend: revenue lines + order dots, oldest → newest left to right. */
function DashboardSundayChart({ sundays, selectedDate, onSelect }) {
  const rows = useMemo(() => [...(sundays || [])].reverse(), [sundays]);
  const W = 720;
  const H = 220;
  const pad = { top: 18, right: 18, bottom: 34, left: 46 };
  const innerW = W - pad.left - pad.right;
  const innerH = H - pad.top - pad.bottom;

  const maxRev = Math.max(1, ...rows.map((s) => Number(s.revenue) || 0));
  const maxOrd = Math.max(1, ...rows.map((s) => Number(s.orders) || 0));
  const n = Math.max(rows.length, 1);

  const xAt = (i) => pad.left + (n === 1 ? innerW / 2 : (i / (n - 1)) * innerW);
  const yRev = (v) => pad.top + innerH - (Number(v) / maxRev) * innerH;
  const yOrd = (v) => pad.top + innerH - (Number(v) / maxOrd) * innerH;

  const totalPts = rows.map((s, i) => ({ x: xAt(i), y: yRev(s.revenue), s }));
  const onlinePts = rows.map((s, i) => ({ x: xAt(i), y: yRev(s.online_revenue) }));
  const counterPts = rows.map((s, i) => ({ x: xAt(i), y: yRev(s.counter_revenue) }));
  const orderPts = rows.map((s, i) => ({ x: xAt(i), y: yOrd(s.orders), s }));

  const line = (pts) => pts.map((p, i) => `${i === 0 ? 'M' : 'L'}${p.x.toFixed(1)},${p.y.toFixed(1)}`).join(' ');
  const area = (pts) => {
    if (!pts.length) return '';
    const base = pad.top + innerH;
    return `${line(pts)} L${pts[pts.length - 1].x.toFixed(1)},${base} L${pts[0].x.toFixed(1)},${base} Z`;
  };

  const gridYs = [0, 0.5, 1].map((t) => ({
    y: pad.top + innerH * (1 - t),
    label: `$${Math.round(maxRev * t)}`,
  }));

  return (
    <div className="dash-chart-svg-wrap">
      <svg
        className="dash-chart-svg"
        viewBox={`0 0 ${W} ${H}`}
        role="img"
        aria-label="Sunday revenue and orders trend"
      >
        <defs>
          <linearGradient id="dashRevFill" x1="0" y1="0" x2="0" y2="1">
            <stop offset="0%" stopColor="#334155" stopOpacity="0.18" />
            <stop offset="100%" stopColor="#334155" stopOpacity="0.02" />
          </linearGradient>
        </defs>

        {gridYs.map((g) => (
          <g key={g.label}>
            <line
              x1={pad.left}
              x2={W - pad.right}
              y1={g.y}
              y2={g.y}
              className="dash-grid-line"
            />
            <text x={pad.left - 8} y={g.y + 4} className="dash-axis-label" textAnchor="end">
              {g.label}
            </text>
          </g>
        ))}

        <path d={area(totalPts)} fill="url(#dashRevFill)" />
        <path d={line(onlinePts)} className="dash-line dash-line-online" fill="none" />
        <path d={line(counterPts)} className="dash-line dash-line-counter" fill="none" />
        <path d={line(totalPts)} className="dash-line dash-line-total" fill="none" />

        {/* Order count as subtle secondary markers */}
        {orderPts.map((p) => (
          <circle
            key={`ord-${p.s.sunday_date}`}
            cx={p.x}
            cy={p.y}
            r={3.5}
            className="dash-order-dot"
          />
        ))}

        {totalPts.map((p) => {
          const selected = p.s.sunday_date === selectedDate;
          return (
            <g key={p.s.sunday_date}>
              {selected && (
                <line
                  x1={p.x}
                  x2={p.x}
                  y1={pad.top}
                  y2={pad.top + innerH}
                  className="dash-selected-rule"
                />
              )}
              <circle
                cx={p.x}
                cy={p.y}
                r={selected ? 6 : 4.5}
                className={`dash-point ${selected ? 'dash-point-selected' : ''}`}
              />
              <text
                x={p.x}
                y={H - 10}
                className={`dash-x-label ${selected ? 'dash-x-label-selected' : ''}`}
                textAnchor="middle"
              >
                {p.s.label.replace(/^Sun\s+/, '')}
              </text>
              <rect
                x={p.x - innerW / (n * 2)}
                y={pad.top}
                width={Math.max(innerW / n, 24)}
                height={innerH}
                className="dash-hit"
                onClick={() => onSelect(p.s.sunday_date)}
              >
                <title>
                  {`${p.s.label}: $${Number(p.s.revenue).toFixed(2)} · ${p.s.orders} orders`}
                </title>
              </rect>
            </g>
          );
        })}
      </svg>
    </div>
  );
}

export default function AdminPage() {
  const navigate = useNavigate();
  const idleWarning = useIdleTimeout('admin');
  const [tab, setTab] = useState('dashboard');
  const [settingsGroup, setSettingsGroup] = useState('general');
  const [settingsShowDetail, setSettingsShowDetail] = useState(false);
  const [categories, setCategories] = useState([]);
  const [items, setItems] = useState([]);
  const [settings, setSettings] = useState({});
  // In-app dialog for App Orders ON / DRINKS / OFF.
  // { mode: 'from-on' | 'from-drinks' | 'from-off' | 'result', title?, body?, busy? }
  const [ordersDialog, setOrdersDialog] = useState(null);
  const [stats, setStats] = useState(null);
  const [dashboard, setDashboard] = useState(null);
  const [dashboardLoading, setDashboardLoading] = useState(false);
  const [dashboardSunday, setDashboardSunday] = useState(null);
  const [dashboardRefresh, setDashboardRefresh] = useState(0);
  const [stuckOrders, setStuckOrders] = useState([]);
  const [showTodayOrders, setShowTodayOrders] = useState(false);
  const [todayOrders, setTodayOrders] = useState([]);
  const [todayOrdersLoading, setTodayOrdersLoading] = useState(false);
  const [docketPrintingId, setDocketPrintingId] = useState(null);
  const [templates, setTemplates] = useState([]);
  const [editingItem, setEditingItem] = useState(null);
  const [editingCat, setEditingCat] = useState(null);
  const [templateName, setTemplateName] = useState('');
  const [venueFilter, setVenueFilter] = useState('all');
  const [menuSearch, setMenuSearch] = useState('');
  const [menuImageBusy, setMenuImageBusy] = useState(false);

  const [report, setReport] = useState(null);
  const [reportFrom, setReportFrom] = useState(() => {
    const d = new Date(); return `${d.getFullYear()}-${String(d.getMonth()+1).padStart(2,'0')}-${String(d.getDate()).padStart(2,'0')}`;
  });
  const [reportTo, setReportTo] = useState(() => {
    const d = new Date(); return `${d.getFullYear()}-${String(d.getMonth()+1).padStart(2,'0')}-${String(d.getDate()).padStart(2,'0')}`;
  });
  // Set of group keys the user has excluded from the report. Empty = include
  // everything (historical behaviour). Chips toggle membership; the "All"
  // chip clears it, "None" fills it with every group key.
  const [reportExclude, setReportExclude] = useState(() => new Set());

  const [schedule, setSchedule] = useState({ enabled: false, entries: [] });
  const [categoryWindows, setCategoryWindows] = useState({});
  const [fastingMode, setFastingMode] = useState(false);

  const [accounts, setAccounts] = useState([]);
  const [accountsLoading, setAccountsLoading] = useState(false);
  const [accountsExporting, setAccountsExporting] = useState(false);
  const [accountSearch, setAccountSearch] = useState('');
  const [accountDetail, setAccountDetail] = useState(null);
  const [selectedUser, setSelectedUser] = useState(null);
  const [userOrders, setUserOrders] = useState([]);
  const [userOrdersLoading, setUserOrdersLoading] = useState(false);

  const [currentAdmin, setCurrentAdmin] = useState(null);
  const [adminAccounts, setAdminAccounts] = useState([]);
  const [adminAccountsLoading, setAdminAccountsLoading] = useState(false);
  const [adminForm, setAdminForm] = useState({
    name: '', email: '', password: '', role: 'admin',
  });
  const [adminBusy, setAdminBusy] = useState(false);
  const [passwordForm, setPasswordForm] = useState({
    currentPassword: '', newPassword: '', confirmPassword: '',
  });
  const [auditRows, setAuditRows] = useState([]);
  const [auditTotal, setAuditTotal] = useState(0);
  const [auditLoading, setAuditLoading] = useState(false);
  const [auditOffset, setAuditOffset] = useState(0);
  const [auditDetail, setAuditDetail] = useState(null);
  const AUDIT_PAGE = 50;
  const [modifierGroups, setModifierGroups] = useState([]);
  const [editingModGroup, setEditingModGroup] = useState(null);
  const [editingModOption, setEditingModOption] = useState(null);
  const [newOptionGroupId, setNewOptionGroupId] = useState(null);
  const [itemModifiers, setItemModifiers] = useState([]);
  const [printAgent, setPrintAgent] = useState({
    print_agent_token: '',
    pending_jobs: 0,
    failed_jobs: 0,
  });
  const [printAgentBusy, setPrintAgentBusy] = useState(false);

  // Counter stations, under Settings. Loaded when that group is opened.
  const [posStations, setPosStations] = useState([]);
  const [posStationsLoading, setPosStationsLoading] = useState(false);
  const [editingStation, setEditingStation] = useState(null); // null | {} for new | row for edit
  const [stationBusy, setStationBusy] = useState(false);

  const refresh = useCallback(async () => {
    const results = await Promise.allSettled([
      api.getAdminCategories().then(setCategories),
      api.getAdminItems().then(setItems),
      api.getSettings().then(setSettings),
      api.getStats().then(setStats),
      api.getStuckPayments().then(setStuckOrders).catch(() => setStuckOrders([])),
      api.getTemplates().then(setTemplates),
      api.getFastingMode().then((r) => setFastingMode(r.fasting)),
      api.getModifierGroups().then(setModifierGroups),
    ]);
    const failed = results.filter((r) => r.status === 'rejected');
    if (failed.length) {
      console.error('Admin refresh failures:', failed.map((f) => f.reason?.message || f.reason));
    }
  }, []);

  const [sessionReady, setSessionReady] = useState(false);

  useEffect(() => {
    let cancelled = false;
    api.getAdminMe()
      .then((session) => { if (!cancelled) setCurrentAdmin(session.admin || null); })
      .catch(() => { if (!cancelled) setCurrentAdmin(null); })
      .finally(() => { if (!cancelled) setSessionReady(true); });
    return () => { cancelled = true; };
  }, []);

  const isSuperAdmin = currentAdmin?.role === 'super_admin';
  const isReporter = currentAdmin?.role === 'reporter';

  useEffect(() => {
    if (!sessionReady) return;
    if (isReporter) {
      api.getStats().then(setStats).catch((e) => console.error('Stats fetch:', e));
      return;
    }
    refresh();
    api.getSchedule().then(setSchedule).catch((e) => console.error('Schedule fetch:', e));
    api.getCategoryWindows().then(setCategoryWindows).catch((e) => console.error('Category windows fetch:', e));
  }, [sessionReady, isReporter, refresh]);

  useEffect(() => {
    if (!isSuperAdmin) return;
    api.getPrintAgentStatus().then(setPrintAgent).catch(() => {});
  }, [isSuperAdmin]);

  useEffect(() => {
    if (isReporter) {
      if (tab !== 'dashboard' && tab !== 'reports') setTab('dashboard');
      return;
    }
    // PIN sessions and regular admins cannot open super-admin screens.
    // Payments, stations, admins, and audit now live inside Settings.
    const movedIntoSettings = new Set(['admins', 'audit', 'stations', 'payments']);
    if (movedIntoSettings.has(tab)) {
      if (!isSuperAdmin) {
        setTab('menu');
        return;
      }
      setSettingsGroup(tab);
      setSettingsShowDetail(true);
      setTab('settings');
      return;
    }
    if (!isSuperAdmin && tab === 'accounts') {
      setTab('menu');
    }
  }, [isSuperAdmin, isReporter, tab]);

  useEffect(() => {
    if (tab !== 'settings' || settingsGroup !== 'admins' || !isSuperAdmin) return;
    let cancelled = false;
    setAdminAccountsLoading(true);
    api.getAdminAccounts()
      .then((rows) => { if (!cancelled) setAdminAccounts(rows); })
      .catch((e) => {
        if (!cancelled) {
          setAdminAccounts([]);
          alert('Failed to load admin accounts: ' + e.message);
        }
      })
      .finally(() => { if (!cancelled) setAdminAccountsLoading(false); });
    return () => { cancelled = true; };
  }, [tab, settingsGroup, isSuperAdmin]);

  useEffect(() => {
    if (tab !== 'settings' || settingsGroup !== 'audit' || !isSuperAdmin) return;
    let cancelled = false;
    setAuditLoading(true);
    api.getAdminAuditLog({ limit: AUDIT_PAGE, offset: auditOffset })
      .then((data) => {
        if (cancelled) return;
        setAuditRows(data.rows || []);
        setAuditTotal(data.total || 0);
      })
      .catch((e) => {
        if (!cancelled) {
          setAuditRows([]);
          setAuditTotal(0);
          alert('Failed to load audit log: ' + e.message);
        }
      })
      .finally(() => { if (!cancelled) setAuditLoading(false); });
    return () => { cancelled = true; };
  }, [tab, settingsGroup, isSuperAdmin, auditOffset]);

  useEffect(() => {
    if (tab !== 'accounts' || !isSuperAdmin) return;
    let cancelled = false;
    setAccountsLoading(true);
    api
      .getAdminUsers()
      .then((rows) => {
        if (!cancelled) setAccounts(rows);
      })
      .catch((e) => {
        if (!cancelled) {
          setAccounts([]);
          alert('Failed to load accounts: ' + e.message);
        }
      })
      .finally(() => {
        if (!cancelled) setAccountsLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [tab, isSuperAdmin]);

  useEffect(() => {
    if (tab !== 'dashboard') return;
    let cancelled = false;
    setDashboardLoading(true);
    api.getAdminDashboard({
      sundays: 12,
      sunday: dashboardSunday || undefined,
    })
      .then((data) => {
        if (cancelled) return;
        setDashboard(data);
        setDashboardSunday((prev) => prev || data.selected_sunday || null);
      })
      .catch((e) => {
        if (!cancelled) {
          console.error('Dashboard fetch:', e);
          alert(e.message || 'Failed to load dashboard');
        }
      })
      .finally(() => {
        if (!cancelled) setDashboardLoading(false);
      });
    return () => { cancelled = true; };
  }, [tab, dashboardSunday, dashboardRefresh]);

  const refreshPosStations = useCallback(() => {
    setPosStationsLoading(true);
    return api
      .getPosStations()
      .then((rows) => setPosStations(Array.isArray(rows) ? rows : []))
      .catch((e) => {
        setPosStations([]);
        console.error('POS stations fetch:', e);
      })
      .finally(() => setPosStationsLoading(false));
  }, []);

  useEffect(() => {
    if (tab === 'settings' && settingsGroup === 'stations') refreshPosStations();
  }, [tab, settingsGroup, refreshPosStations]);

  async function handleStationSubmit(e) {
    e.preventDefault();
    if (stationBusy) return;
    const form = new FormData(e.currentTarget);
    const payload = {
      name: form.get('name'),
      station_key: form.get('station_key') || undefined,
      tyro_terminal_id: form.get('tyro_terminal_id') || '',
      printer_ip: form.get('printer_ip') || '',
      printer_port: form.get('printer_port') || '9100',
    };
    setStationBusy(true);
    try {
      if (editingStation && editingStation.id) {
        await api.updatePosStation(editingStation.id, {
          ...payload,
          active: form.get('active') === 'on',
        });
      } else {
        await api.createPosStation(payload);
      }
      setEditingStation(null);
      await refreshPosStations();
    } catch (err) {
      alert('Failed to save station: ' + err.message);
    } finally {
      setStationBusy(false);
    }
  }

  async function handleStationActive(row, next) {
    if (!next) {
      if (!window.confirm(`Deactivate station "${row.name}"? It will hide from the picker but historical orders keep their reference.`)) return;
      try {
        await api.deletePosStation(row.id);
        await refreshPosStations();
      } catch (err) {
        alert('Failed to deactivate station: ' + err.message);
      }
      return;
    }
    try {
      await api.updatePosStation(row.id, { active: true });
      await refreshPosStations();
    } catch (err) {
      alert('Failed to activate station: ' + err.message);
    }
  }

  function toggleOrders() {
    // Only affects the mobile / web ordering channel — counter POS sales
    // always go through. Server-side carve-out lives in POST /api/orders.
    const mode = settings.accepting_orders;
    if (mode === '1') {
      setOrdersDialog({ mode: 'from-on', busy: false });
    } else if (mode === 'drinks') {
      setOrdersDialog({ mode: 'from-drinks', busy: false });
    } else {
      setOrdersDialog({ mode: 'from-off', busy: false });
    }
  }

  async function applyOrdersOff() {
    setOrdersDialog((d) => (d ? { ...d, busy: true } : d));
    try {
      await api.updateSettings({ accepting_orders: '0' });
      setSettings((s) => ({ ...s, accepting_orders: '0' }));
      setOrdersDialog(null);
    } catch (err) {
      setOrdersDialog({
        mode: 'result',
        title: 'Could not update',
        body: err.message || 'Failed to turn orders off.',
        busy: false,
      });
    }
  }

  async function applyOrdersDrinks() {
    setOrdersDialog((d) => (d ? { ...d, busy: true } : d));
    try {
      await api.updateSettings({ accepting_orders: 'drinks' });
      setSettings((s) => ({ ...s, accepting_orders: 'drinks' }));
      setOrdersDialog(null);
    } catch (err) {
      setOrdersDialog({
        mode: 'result',
        title: 'Could not update',
        body: err.message || 'Failed to switch to drinks only.',
        busy: false,
      });
    }
  }

  async function applyOrdersOn({ notify }) {
    setOrdersDialog((d) => (d ? { ...d, busy: true } : d));
    try {
      await api.updateSettings({ accepting_orders: '1' });
      setSettings((s) => ({ ...s, accepting_orders: '1' }));
      if (!notify) {
        setOrdersDialog(null);
        return;
      }
      try {
        const result = await api.notifyOrdersOpen();
        const sent = result?.sent ?? 0;
        const detail = `iOS: ${result?.ios ?? 0}, Android: ${result?.android ?? 0}` +
          (result?.expired ? `, removed ${result.expired} expired token(s)` : '');
        if (sent === 0) {
          setOrdersDialog({
            mode: 'result',
            title: 'Orders are ON',
            body:
              'No devices were notified.\n\n' +
              (result?.message ||
                'Customers need to open the app and allow notifications at least once.'),
            busy: false,
          });
        } else {
          setOrdersDialog({
            mode: 'result',
            title: 'Orders are ON',
            body: `Notification sent to ${sent} device(s) (${detail}).`,
            busy: false,
          });
        }
      } catch (err) {
        setOrdersDialog({
          mode: 'result',
          title: 'Orders are ON',
          body:
            'Notification failed: ' + (err.message || 'unknown error'),
          busy: false,
        });
      }
    } catch (err) {
      setOrdersDialog({
        mode: 'result',
        title: 'Could not update',
        body: err.message || 'Failed to turn orders on.',
        busy: false,
      });
    }
  }

  async function handleResetDay() {
    if (!window.confirm(
      'Reset day? This closes EVERY active order (any date) so customer confirmation pages clear, and restarts order numbering (B1/C1/N1) for the new day. It also clears failed cafe sticker jobs so they are not reprinted later. Stock quantities are left as-is. Stuck payments are not affected — use the Clear button for those.'
    )) return;
    const result = await api.resetDaily();
    refresh();
    if (showTodayOrders) await loadTodayOrders();
    try {
      if (currentAdmin?.role === 'super_admin') {
        const status = await api.getPrintAgentStatus();
        setPrintAgent(status);
      }
    } catch {
      // Print-agent panel may not have loaded yet.
    }
    alert(
      `Day reset complete. ${result?.orders_closed ?? 0} active order(s) closed` +
        `, ${result?.failed_print_jobs_cleared ?? 0} failed sticker job(s) cleared.`
    );
  }

  async function handleClearStuckPayments() {
    if (!window.confirm('Cancel all orders stuck in "awaiting payment"? This cannot be undone.')) return;
    try {
      const result = await api.clearStuckPayments();
      alert(`Cleared ${result.cleared} stuck order(s).`);
      refresh();
    } catch (err) {
      alert('Failed: ' + err.message);
    }
  }

  async function handleClearIOSBadges() {
    if (!window.confirm('Send a silent push to every registered iPhone to reset the app icon badge to 0?')) return;
    try {
      const result = await api.clearIOSBadges();
      alert(`Sent silent badge-clear to ${result.cleared} iPhone${result.cleared === 1 ? '' : 's'}.` +
        (result.expired ? ` ${result.expired} stale token${result.expired === 1 ? '' : 's'} removed.` : ''));
    } catch (err) {
      alert('Failed: ' + err.message);
    }
  }

  async function loadTodayOrders() {
    setTodayOrdersLoading(true);
    try {
      const rows = await api.getTodayOrders();
      setTodayOrders(rows);
    } catch (err) {
      setTodayOrders([]);
      alert('Failed to load orders: ' + err.message);
    } finally {
      setTodayOrdersLoading(false);
    }
  }

  async function toggleTodayOrders() {
    if (showTodayOrders) {
      setShowTodayOrders(false);
      return;
    }
    setShowTodayOrders(true);
    await loadTodayOrders();
  }

  async function handlePrintDocket(order) {
    if (docketPrintingId) return;
    setDocketPrintingId(order.id);
    try {
      const { isNativePrintAvailable, printDocketsNative } = await import('../lib/nativePrint');
      const native = isNativePrintAvailable();
      const result = await api.printKitchenDocket({
        orderId: order.id,
        clientPrints: native,
      });
      if (native && result.dockets?.length) {
        const printed = await printDocketsNative(result.dockets);
        if (printed.failed?.length) {
          alert(`Docket print failed for ${order.order_number}: ${printed.failed.map((f) => f.error).join(', ')}`);
        } else {
          alert(`Printed docket${result.dockets.length > 1 ? 's' : ''} for ${(result.order_numbers || [order.order_number]).join(', ')}`);
        }
        return;
      }
      if (result.printed > 0) {
        alert(result.message || `Printed docket for ${order.order_number}`);
        return;
      }
      alert(
        (result.message ? `${result.message}\n\n` : '') +
        `This computer cannot reach the docket printer. On the St Barb POS iPad, type ${order.order_number} in Reprint and tap Print.`
      );
    } catch (err) {
      alert(err.message || 'Print failed');
    } finally {
      setDocketPrintingId(null);
    }
  }

  function orderCustomerLabel(o) {
    const guest = o.customer_name || 'Guest';
    if (o.account_name && o.account_name !== guest) {
      return `${guest} (${o.account_name})`;
    }
    if (o.account_email && !o.account_name) {
      return `${guest} (${o.account_email})`;
    }
    return guest;
  }

  async function handleDeactivate(scope) {
    const scopeLabel = scope === 'drinks' ? 'drinks' : 'food';
    // Match the server's category predicate exactly so the count in the
    // confirm dialog is accurate: an item is "drinks" iff its category has
    // is_drinks = 1; everything else (kitchen + cafe non-drinks) is "food".
    const activeItems = items.filter((i) => {
      if (!i.active || i.is_adhoc === 1) return false;
      const isDrinks = i.is_drinks === 1;
      return scope === 'drinks' ? isDrinks : !isDrinks;
    });
    if (activeItems.length === 0) {
      alert(`No active ${scopeLabel} items to deactivate.`);
      return;
    }
    const detail = scope === 'drinks'
      ? 'Cafe drink categories (Hot Drinks, Cold Drinks, and any you have flagged as "Drinks").'
      : 'Kitchen items and cafe food — everything except drinks.';
    if (
      !window.confirm(
        `Deactivate all ${activeItems.length} active ${scopeLabel} item(s)?\n\n${detail}\n\nAd-hoc items are not changed. Customers will not see these items until you turn them back on or load a template.`
      )
    ) {
      return;
    }
    try {
      const result = await api.deactivateMenuItems(scope);
      alert(`Deactivated ${result.deactivated} ${scopeLabel} item(s).`);
      refresh();
    } catch (err) {
      alert('Failed: ' + err.message);
    }
  }

  async function handleViewUserOrders(user) {
    setSelectedUser(user);
    setUserOrders([]);
    setUserOrdersLoading(true);
    try {
      const orders = await api.getAdminUserOrders(user.id);
      setUserOrders(orders);
    } catch (err) {
      alert('Failed to load orders: ' + err.message);
    } finally {
      setUserOrdersLoading(false);
    }
  }

  function patchAccount(userId, patch) {
    setAccounts((prev) => prev.map((a) => (a.id === userId ? { ...a, ...patch } : a)));
    setAccountDetail((prev) => (prev && prev.id === userId ? { ...prev, ...patch } : prev));
  }

  async function handleDeleteUser(user) {
    if (!window.confirm(`Permanently delete the account for "${user.name}" (${user.email})? Their past orders will remain in the system.`)) return;
    try {
      await api.deleteAdminUser(user.id);
      setAccounts((prev) => prev.filter((a) => a.id !== user.id));
      setAccountDetail((prev) => (prev?.id === user.id ? null : prev));
    } catch (err) {
      alert('Failed: ' + err.message);
    }
  }

  async function handleVerifyUser(user) {
    if (!window.confirm(`Manually verify email for "${user.name}" (${user.email})?`)) return;
    try {
      await api.verifyAdminUser(user.id);
      patchAccount(user.id, { email_verified: 1 });
    } catch (err) {
      alert('Failed: ' + err.message);
    }
  }

  async function handleSundaySchoolStatus(user, status, action = null) {
    const labels = {
      approved: `Approve "${user.name}" as a Sunday School servant? Their cafe orders will show SUNDAY SCHOOL on stickers.`,
      none:
        action === 'reject'
          ? `Reject Sunday School servant request from "${user.name}"? They will not be flagged as Sunday School.`
          : `Remove Sunday School servant status from "${user.name}"? Future orders will no longer be flagged.`,
      pending: `Mark "${user.name}" as pending Sunday School approval?`,
    };
    if (!window.confirm(labels[status] || `Set Sunday School status to ${status}?`)) return;
    try {
      await api.setAdminUserSundaySchool(user.id, status);
      patchAccount(user.id, { sunday_school_status: status });
    } catch (err) {
      alert('Failed: ' + err.message);
    }
  }

  const filteredAccounts = accounts.filter((u) => {
    if (!accountSearch.trim()) return true;
    const q = accountSearch.toLowerCase();
    return u.name.toLowerCase().includes(q) || u.email.toLowerCase().includes(q);
  });

  async function loadReport() {
    try {
      const exclude = Array.from(reportExclude).join(',');
      const params = { from: reportFrom, to: reportTo };
      if (exclude) params.exclude = exclude;
      const data = await api.getSalesReport(params);
      setReport(data);
    } catch (err) {
      alert('Failed to generate report: ' + (err.message || 'unknown error'));
    }
  }

  function toggleReportGroup(key) {
    setReportExclude((prev) => {
      const next = new Set(prev);
      if (next.has(key)) next.delete(key);
      else next.add(key);
      return next;
    });
  }

  async function saveSchedule() {
    await api.updateSchedule(schedule);
    alert('Schedule saved! The server checks every minute.');
  }

  async function saveCategoryWindows() {
    try {
      await api.updateCategoryWindows(categoryWindows);
      alert('Category order windows saved!');
    } catch (err) {
      alert('Failed to save: ' + err.message);
    }
  }

  const defaultWindow = { enabled: false, available_from: '10:00', available_until: '15:00' };

  function updateCategoryWindow(catName, field, value) {
    setCategoryWindows((prev) => ({
      ...prev,
      [catName]: { ...defaultWindow, ...(prev[catName] || {}), [field]: value },
    }));
  }

  function addScheduleEntry() {
    setSchedule((s) => ({
      ...s,
      entries: [...s.entries, { days: ['sunday'], start: '07:00', end: '11:30', menu: 'drinks', closingReminder: true }],
    }));
  }

  function removeScheduleEntry(idx) {
    setSchedule((s) => ({
      ...s,
      entries: s.entries.filter((_, i) => i !== idx),
    }));
  }

  function updateScheduleEntry(idx, field, value) {
    setSchedule((s) => ({
      ...s,
      entries: s.entries.map((e, i) => (i === idx ? { ...e, [field]: value } : e)),
    }));
  }

  function toggleScheduleDay(idx, day) {
    setSchedule((s) => ({
      ...s,
      entries: s.entries.map((e, i) => {
        if (i !== idx) return e;
        const days = e.days.includes(day) ? e.days.filter((d) => d !== day) : [...e.days, day];
        return { ...e, days };
      }),
    }));
  }

  const [imageFile, setImageFile] = useState(null);
  const [uploading, setUploading] = useState(false);

  async function openEditItem(item) {
    setEditingItem(item);
    if (item.id) {
      try {
        const mods = await api.getItemModifiers(item.id);
        setItemModifiers(mods);
      } catch { setItemModifiers([]); }
    } else {
      setItemModifiers([]);
    }
  }

  function isModifierAssigned(groupId) {
    return itemModifiers.some((m) => m.modifier_group_id === groupId);
  }

  function toggleModifier(groupId, checked) {
    if (checked) {
      const maxOrder = itemModifiers.reduce((max, m) => Math.max(max, m.sort_order || 0), -1);
      setItemModifiers((prev) => [...prev, { modifier_group_id: groupId, sort_order: maxOrder + 1 }]);
    } else {
      setItemModifiers((prev) => prev.filter((m) => m.modifier_group_id !== groupId));
    }
  }

  function moveModifier(groupId, direction) {
    setItemModifiers((prev) => {
      const sorted = [...prev].sort((a, b) => (a.sort_order || 0) - (b.sort_order || 0));
      const idx = sorted.findIndex((m) => m.modifier_group_id === groupId);
      if (idx < 0) return prev;
      const swapIdx = idx + direction;
      if (swapIdx < 0 || swapIdx >= sorted.length) return prev;
      const temp = sorted[idx].sort_order;
      sorted[idx] = { ...sorted[idx], sort_order: sorted[swapIdx].sort_order };
      sorted[swapIdx] = { ...sorted[swapIdx], sort_order: temp };
      return sorted;
    });
  }

  async function saveItem(e) {
    e.preventDefault();
    const form = new FormData(e.target);
    const data = {
      category_id: Number(form.get('category_id')),
      name: form.get('name'),
      description: form.get('description'),
      price: parseFloat(form.get('price')),
      sort_order: Number(form.get('sort_order') || 0),
      stock_quantity: (() => {
        const raw = String(form.get('stock_quantity') || '').replace(/[^\d]/g, '');
        return raw !== '' ? Number(raw) : null;
      })(),
      is_adhoc: form.get('is_adhoc') === 'on' ? 1 : 0,
      variable_price: form.get('variable_price') === 'on' ? 1 : 0,
      diet_type: form.get('diet_type') || 'non_fasting',
      max_per_user: form.get('max_per_user') !== '' ? Number(form.get('max_per_user')) : null,
    };

    let savedItem;
    if (editingItem?.id) {
      savedItem = await api.updateItem(editingItem.id, data);
    } else {
      savedItem = await api.createItem(data);
    }

    if (imageFile && savedItem?.id) {
      setUploading(true);
      try {
        await api.uploadItemImage(savedItem.id, imageFile);
      } catch (err) {
        alert('Item saved but image upload failed: ' + err.message);
      }
      setUploading(false);
    }

    if (savedItem?.id) {
      try {
        await api.updateItemModifiers(savedItem.id, itemModifiers.sort((a, b) => (a.sort_order || 0) - (b.sort_order || 0)));
      } catch (err) {
        alert('Item saved but modifier assignment failed: ' + err.message);
      }
    }

    setImageFile(null);
    setEditingItem(null);
    setItemModifiers([]);
    refresh();
  }

  async function handleDeleteImage(itemId) {
    if (!window.confirm('Remove this item\'s photo?')) return;
    await api.deleteItemImage(itemId);
    refresh();
  }

  async function handleDeleteItem(item) {
    if (!window.confirm(`Permanently delete "${item.name}"? This cannot be undone.`)) return;
    await api.deleteItem(item.id);
    refresh();
  }

  const [dragIdx, setDragIdx] = useState(null);
  const [dragOverIdx, setDragOverIdx] = useState(null);
  const [dragType, setDragType] = useState(null);

  function handleDragStart(type, idx) {
    setDragType(type);
    setDragIdx(idx);
  }

  function handleDragOver(e, idx) {
    e.preventDefault();
    setDragOverIdx(idx);
  }

  function handleDragEnd() {
    setDragIdx(null);
    setDragOverIdx(null);
    setDragType(null);
  }

  async function handleDrop(type, list, dropIdx) {
    if (dragIdx === null || dragIdx === dropIdx) { handleDragEnd(); return; }
    const reordered = [...list];
    const [moved] = reordered.splice(dragIdx, 1);
    reordered.splice(dropIdx, 0, moved);
    const updates = reordered.map((item, i) => ({ id: item.id, sort_order: i }));
    handleDragEnd();
    if (type === 'item') {
      await api.reorderItems(updates);
    } else {
      await api.reorderCategories(updates);
    }
    refresh();
  }

  async function saveCat(e) {
    e.preventDefault();
    const form = new FormData(e.target);
    const venue = form.get('venue') || 'kitchen';
    // `is_drinks` is only meaningful for the cafe. Force it off for kitchen
    // categories so a stale checkbox tick can't leak into the DB.
    const isDrinks = venue === 'cafe' && form.get('is_drinks') === 'on' ? 1 : 0;
    // Hot-drink routing is only relevant for cafe drinks. Server also
    // guards this, but zeroing here keeps the payload honest.
    const isHotDrink = isDrinks && form.get('is_hot_drink') === 'on' ? 1 : 0;
    const data = {
      name: form.get('name'),
      sort_order: Number(form.get('sort_order') || 0),
      venue,
      kitchen: venue === 'kitchen' ? (form.get('kitchen') || null) : null,
      is_drinks: isDrinks,
      is_hot_drink: isHotDrink,
      collected_at_counter: venue === 'desserts' ? 0 : (form.get('collected_at_counter') === 'on' ? 1 : 0),
    };
    if (editingCat?.id) {
      await api.updateCategory(editingCat.id, data);
    } else {
      await api.createCategory(data);
    }
    setEditingCat(null);
    refresh();
  }

  async function toggleItemActive(item) {
    const currentlyOn = Number(item.active) === 1;
    try {
      await api.updateItem(item.id, { active: currentlyOn ? 0 : 1 });
      await refresh();
    } catch (err) {
      alert('Could not update item: ' + (err.message || 'unknown error'));
    }
  }

  async function toggleCatActive(cat) {
    await api.updateCategory(cat.id, { active: cat.active ? 0 : 1 });
    refresh();
  }

  async function commitItemStock(itemId, qty) {
    const updated = await api.updateItemStock(itemId, qty);
    const next = updated && Object.prototype.hasOwnProperty.call(updated, 'stock_quantity')
      ? updated.stock_quantity
      : qty;
    setItems((prev) => prev.map((row) => (
      row.id === itemId ? { ...row, stock_quantity: next } : row
    )));
  }

  async function commitOptionStock(optionId, qty) {
    const updated = await api.updateModifierOption(optionId, { stock_quantity: qty });
    const next = updated && Object.prototype.hasOwnProperty.call(updated, 'stock_quantity')
      ? updated.stock_quantity
      : qty;
    setItems((prev) => prev.map((row) => {
      if (!row.stock_modifier_groups) return row;
      return {
        ...row,
        stock_modifier_groups: row.stock_modifier_groups.map((group) => ({
          ...group,
          options: (group.options || []).map((opt) => (
            opt.id === optionId ? { ...opt, stock_quantity: next } : opt
          )),
        })),
      };
    }));
  }

  async function handleSaveTemplate() {
    if (!templateName.trim()) return;
    await api.saveTemplate(templateName.trim());
    setTemplateName('');
    refresh();
  }

  async function handleLoadTemplate(id) {
    if (!window.confirm('Load this template? Current non-adhoc items will be replaced.')) return;
    await api.loadTemplate(id);
    refresh();
  }

  async function handleDeleteTemplate(id) {
    if (!window.confirm('Delete this template?')) return;
    await api.deleteTemplate(id);
    refresh();
  }

  async function handleSaveSettings(e) {
    e.preventDefault();
    const form = new FormData(e.target);
    const data = {};
    const pinFields = ['admin_pin', 'cafe_pin', 'kitchen_pin', 'counter_pin'];
    for (const [key, value] of form.entries()) {
      if (pinFields.includes(key) && !value.trim()) continue;
      data[key] = value;
    }
    try {
      await api.updateSettings(data);
      // Re-read from server so the UI matches what POS will see (avoids
      // stale defaultValue / partial merge mismatches).
      const fresh = await api.getSettings();
      setSettings(fresh);
      const provider = fresh.eftpos_provider || data.eftpos_provider || 'manual';
      alert(
        provider === 'tyro_pay_instore'
          ? 'Payments saved. EFTPOS provider is Tyro Pay Instore — reopen or refresh the POS to pick it up.'
          : 'Settings saved!'
      );
    } catch (err) {
      alert('Save failed: ' + (err.message || 'unknown error'));
    }
  }

  const handleGenerateMenuImage = async () => {
    if (menuImageBusy) return;
    setMenuImageBusy(true);
    try {
      const [freshItems, groups] = await Promise.all([
        api.getAdminItems(),
        api.getModifierGroups(),
      ]);
      setItems(freshItems);
      setModifierGroups(groups);
      const grouped = groupFoodMenuForFlyer(freshItems, categories, {
        fasting: fastingMode,
        modifierGroups: groups,
      });
      if (!grouped.barbara.length && !grouped.noufer.length && !grouped.cafe.length) {
        alert('No active food items to put on the menu. Turn items on first. Drinks are never included.');
        return;
      }
      await downloadFoodMenuImage(grouped);
    } catch (err) {
      alert(err.message || 'Could not generate menu image');
    } finally {
      setMenuImageBusy(false);
    }
  };

  const activeCategories = categories.filter((c) => c.active);

  const filteredItems = items.filter((i) => {
    if (venueFilter !== 'all' && i.venue !== venueFilter) return false;
    if (menuSearch.trim()) {
      const q = menuSearch.toLowerCase();
      return (
        i.name.toLowerCase().includes(q) ||
        (i.category_name || '').toLowerCase().includes(q)
      );
    }
    return true;
  }).sort((a, b) => (b.active ? 1 : 0) - (a.active ? 1 : 0));

  const filteredCategories = venueFilter === 'all'
    ? categories
    : categories.filter((c) => c.venue === venueFilter);

  return (
    <main className="admin-page">
      {idleWarning && (
        <div className="idle-warning">
          Logging out in 1 minute due to inactivity — tap anywhere to stay logged in
        </div>
      )}
      <div className="admin-header">
        <div className="admin-header-brand">
          <img
            src="/logo.png"
            alt="St Barbara & St Noufer"
            className="admin-header-logo"
          />
          <h1>Admin Panel</h1>
        </div>
        <div className="admin-header-actions">
          {!isReporter && (
          <button
            className={`btn btn-sm ${
              settings.accepting_orders === '1'
                ? 'btn-success'
                : settings.accepting_orders === 'drinks'
                  ? 'btn-warning'
                  : 'btn-danger'
            }`}
            onClick={toggleOrders}
            title="Toggles the mobile app / online ordering channel. Counter POS sales are not affected."
          >
            App Orders: {
              settings.accepting_orders === '1'
                ? 'ON'
                : settings.accepting_orders === 'drinks'
                  ? 'DRINKS'
                  : 'OFF'
            }
          </button>
          )}
          {currentAdmin && (
            <span className="admin-signed-in" title={currentAdmin.email}>
              {currentAdmin.name}
            </span>
          )}
          <button
            className="btn btn-sm btn-outline logout-btn"
            onClick={async () => {
              try { await api.adminLogout(); } catch { /* ignore */ }
              sessionStorage.removeItem('pin-auth-admin');
              sessionStorage.removeItem('auth-token');
              navigate('/');
            }}
          >
            Logout
          </button>
        </div>
      </div>

      {stats && (
        <div className="admin-stats-row">
          <div className="admin-stats">
            <button
              type="button"
              className={`stat-card card stat-card-clickable ${showTodayOrders ? 'stat-card-active' : ''}`}
              onClick={toggleTodayOrders}
              title="View order details"
            >
              <span className="stat-value">{stats.today_orders}</span>
              <span className="stat-label">Completed Orders</span>
              <span className="stat-hint">{showTodayOrders ? 'Hide details' : 'Tap for details'}</span>
            </button>
            <div className="stat-card card">
              <span className="stat-value">${Number(stats.today_revenue || 0).toFixed(2)}</span>
              <span className="stat-label">Revenue Today</span>
              {(stats.today_revenue_online !== undefined || stats.today_revenue_counter !== undefined) && (
                <span className="stat-hint" style={{ display: 'block', marginTop: 4, fontSize: 12, lineHeight: 1.5 }}>
                  Online: ${Number(stats.today_revenue_online || 0).toFixed(2)}
                  {' · '}
                  Counter: ${Number(stats.today_revenue_counter || 0).toFixed(2)}
                  {' · '}
                  Refunded: ${Number(stats.today_revenue_refunded || 0).toFixed(2)}
                </span>
              )}
            </div>
            <div className="stat-card card">
              <span className="stat-value">{stats.active_orders}</span>
              <span className="stat-label">In Progress</span>
            </div>
            {stats.stuck_payments > 0 && (
              <div className="stat-card card stat-card-warning">
                <span className="stat-value">{stats.stuck_payments}</span>
                <span className="stat-label">Stuck Payments</span>
                {!isReporter && (
                <button className="btn btn-sm btn-danger" style={{ marginTop: 6 }} onClick={handleClearStuckPayments}>
                  Clear
                </button>
                )}
              </div>
            )}
          </div>
          {showTodayOrders && (
            <div className="today-orders-detail">
              <div className="today-orders-header">
                <h4>Completed Orders (today)</h4>
                <p className="admin-hint" style={{ margin: '4px 0 0', flex: 1 }}>
                  {isReporter
                    ? 'Orders completed in today’s service period.'
                    : 'Print docket sends to the kitchen thermal printer. On a laptop this will tell you to reprint from the POS iPad.'}
                </p>
                <button
                  type="button"
                  className="btn btn-sm btn-outline"
                  onClick={loadTodayOrders}
                  disabled={todayOrdersLoading}
                >
                  {todayOrdersLoading ? 'Loading…' : 'Refresh'}
                </button>
              </div>
              {todayOrdersLoading && todayOrders.length === 0 ? (
                <p className="today-orders-empty">Loading orders…</p>
              ) : todayOrders.length === 0 ? (
                <p className="today-orders-empty">No orders in this period.</p>
              ) : (
                <div className="admin-table-wrap">
                  <table className="admin-table">
                    <thead>
                      <tr>
                        <th>Order #</th>
                        <th>Customer</th>
                        <th>Source</th>
                        <th>Time</th>
                        <th>Items</th>
                        <th>Total</th>
                        <th>Status</th>
                        {!isReporter && <th>Actions</th>}
                      </tr>
                    </thead>
                    <tbody>
                      {todayOrders.map((o) => (
                        <tr key={o.id}>
                          <td><strong>{o.order_number}</strong></td>
                          <td>{orderCustomerLabel(o)}</td>
                          <td>
                            {o.channel === 'counter' ? (
                              <span className="badge badge-counter" title={o.station_name || undefined}>
                                Counter{o.station_name ? ` · ${o.station_name}` : ''}
                              </span>
                            ) : (
                              <span className="badge badge-online">Online</span>
                            )}
                          </td>
                          <td>{fmtAU(parseDbDateTime(o.created_at))}</td>
                          <td>
                            {(o.items || []).map((i) => `${i.quantity}x ${i.item_name}`).join(', ')}
                            {o.notes ? (
                              <div className="order-notes-line">Note: {o.notes}</div>
                            ) : null}
                          </td>
                          <td>${o.total.toFixed(2)}</td>
                          <td>
                            <span className={`badge badge-${o.status}`}>{o.status}</span>
                          </td>
                          {!isReporter && (
                          <td>
                            {o.venue === 'cafe' ? (
                              <span className="admin-hint">Sticker</span>
                            ) : (
                              <button
                                type="button"
                                className="btn btn-xs btn-outline"
                                disabled={docketPrintingId === o.id}
                                onClick={() => handlePrintDocket(o)}
                              >
                                {docketPrintingId === o.id ? 'Printing…' : 'Print docket'}
                              </button>
                            )}
                          </td>
                          )}
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          )}
          {stuckOrders && stuckOrders.length > 0 && (
            <div className="stuck-orders-detail" style={{ marginTop: 12 }}>
              <h4 style={{ marginBottom: 8 }}>Stuck Payment Details</h4>
              {stuckOrders.map((o) => (
                <div key={o.id} className="card" style={{ padding: 10, marginBottom: 8 }}>
                  <strong>{o.order_number}</strong> &mdash; {o.customer_name || 'Guest'}
                  <br />
                  <span style={{ fontSize: 13, color: '#666' }}>
                    {o.items_summary} &bull; ${o.total.toFixed(2)} &bull; {o.created_at}
                  </span>
                </div>
              ))}
            </div>
          )}
          {!isReporter && (
          <>
          <button className="btn btn-sm btn-danger reset-day-btn" onClick={handleResetDay}>
            Reset Day
          </button>
          <button
            className="btn btn-sm btn-outline reset-day-btn"
            style={{ marginLeft: 8 }}
            onClick={handleClearIOSBadges}
            title="Send a silent push to every iPhone with badge=0 to clear any stuck app-icon badges."
          >
            Clear iOS Badges
          </button>
          </>
          )}
        </div>
      )}

      <div className="admin-tabs">
        {(isReporter
          ? [
            ['dashboard', 'Dashboard'],
            ['reports', 'Reports'],
          ]
          : [
          ['dashboard', 'Dashboard'],
          ['menu', 'Menu Items'],
          ['categories', 'Categories'],
          ['modifiers', 'Modifiers'],
          ['templates', 'Templates'],
          ...(isSuperAdmin ? [['accounts', 'User Accounts']] : []),
          ['reports', 'Reports'],
          ['settings', 'Settings'],
        ]).map(([t, label]) => (
          <button
            key={t}
            className={`btn btn-sm ${tab === t ? 'btn-primary' : 'btn-outline'}`}
            onClick={() => setTab(t)}
          >
            {label}
          </button>
        ))}
      </div>

      {(tab === 'menu' || tab === 'categories') && (
        <div className="admin-venue-filter">
          {['all', 'kitchen', 'cafe', 'desserts'].map((v) => (
            <button
              key={v}
              className={`btn btn-sm ${venueFilter === v ? 'btn-primary' : 'btn-outline'}`}
              onClick={() => setVenueFilter(v)}
            >
              {v === 'all' ? 'All' : VENUE_LABELS[v] || v}
            </button>
          ))}
        </div>
      )}

      {tab === 'dashboard' && (
        <section className="admin-section dashboard-section">
          <div className="admin-section-header">
            <h2>Sunday dashboard</h2>
            <button
              type="button"
              className="btn btn-sm btn-outline"
              disabled={dashboardLoading}
              onClick={() => setDashboardRefresh((n) => n + 1)}
            >
              {dashboardLoading ? 'Loading…' : 'Refresh'}
            </button>
          </div>
          <p className="admin-hint">
            Each Sunday includes <strong>Saturday + Sunday</strong> local orders (overnight online + Sunday service).
            Tap a Sunday to see its top items. Live cards above are this service day since last Reset day.
          </p>

          {dashboardLoading && !dashboard ? (
            <p className="admin-hint">Loading dashboard…</p>
          ) : dashboard ? (
            <>
              <div className="dash-chart card">
                <div className="dash-chart-head">
                  <h3 className="dash-chart-title">Sunday trend</h3>
                  <div className="dash-legend">
                    <span><i className="dash-swatch dash-swatch-total" /> Revenue</span>
                    <span><i className="dash-swatch dash-swatch-online" /> Online $</span>
                    <span><i className="dash-swatch dash-swatch-counter" /> Counter $</span>
                    <span><i className="dash-swatch dash-swatch-orders" /> Orders</span>
                  </div>
                </div>
                <DashboardSundayChart
                  sundays={dashboard.sundays}
                  selectedDate={dashboardSunday || dashboard.selected_sunday}
                  onSelect={setDashboardSunday}
                />
                <p className="dash-chart-footnote admin-hint">
                  Tap a Sunday on the chart to load its top items. Grey dots = order count (separate scale).
                </p>
              </div>

              {(() => {
                const selected = (dashboard.sundays || []).find(
                  (s) => s.sunday_date === (dashboardSunday || dashboard.selected_sunday)
                );
                if (!selected) return null;
                return (
                  <div className="dash-selected-strip">
                    <div className="dash-selected-label">
                      <span className="dash-selected-kicker">Selected</span>
                      <strong>{selected.label}</strong>
                    </div>
                    <div className="dash-selected-metrics">
                      <div className="dash-metric">
                        <span className="dash-metric-value">${Number(selected.revenue).toFixed(2)}</span>
                        <span className="dash-metric-label">Revenue</span>
                      </div>
                      <div className="dash-metric">
                        <span className="dash-metric-value">{selected.orders}</span>
                        <span className="dash-metric-label">Orders</span>
                      </div>
                      <div className="dash-metric">
                        <span className="dash-metric-value">${Number(selected.online_revenue).toFixed(2)}</span>
                        <span className="dash-metric-label">Online</span>
                      </div>
                      <div className="dash-metric">
                        <span className="dash-metric-value">${Number(selected.counter_revenue).toFixed(2)}</span>
                        <span className="dash-metric-label">Counter</span>
                      </div>
                    </div>
                  </div>
                );
              })()}

              <div className="card dash-top-items">
                <div className="admin-section-header" style={{ marginBottom: 12 }}>
                  <h3 style={{ margin: 0 }}>
                    Top items —{' '}
                    {(dashboard.sundays || []).find(
                      (s) => s.sunday_date === (dashboardSunday || dashboard.selected_sunday)
                    )?.label || dashboard.selected_sunday}
                  </h3>
                </div>
                {(dashboard.top_items || []).length === 0 ? (
                  <p className="admin-hint">No items sold that weekend.</p>
                ) : (
                  <div className="admin-table-wrap">
                    <table className="admin-table">
                      <thead>
                        <tr>
                          <th>Item</th>
                          <th>Qty</th>
                          <th>Revenue</th>
                          <th>Online</th>
                          <th>Counter</th>
                        </tr>
                      </thead>
                      <tbody>
                        {dashboard.top_items.map((item) => (
                          <tr key={`${item.item_name}-${item.category_name}`}>
                            <td>
                              <strong>{item.item_name}</strong>
                              <div className="admin-hint" style={{ margin: 0 }}>{item.category_name}</div>
                            </td>
                            <td>{item.total_qty}</td>
                            <td>${Number(item.total_revenue).toFixed(2)}</td>
                            <td>
                              {item.online_qty}
                              <span className="admin-hint"> · ${Number(item.online_revenue).toFixed(2)}</span>
                            </td>
                            <td>
                              {item.counter_qty}
                              <span className="admin-hint"> · ${Number(item.counter_revenue).toFixed(2)}</span>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}
              </div>
            </>
          ) : (
            <p className="admin-hint">No dashboard data yet.</p>
          )}
        </section>
      )}

      {tab === 'menu' && (
        <section className="admin-section">
          <div className="admin-section-header">
            <h2>Menu Items</h2>
            <div className="admin-section-header-actions">
              <button
                type="button"
                className="btn btn-sm btn-danger"
                onClick={() => handleDeactivate('food')}
                title="Deactivate kitchen items and cafe food — everything except drinks."
              >
                Deactivate Food
              </button>
              <button
                type="button"
                className="btn btn-sm btn-danger"
                onClick={() => handleDeactivate('drinks')}
                title="Deactivate cafe drink categories (Hot Drinks, Cold Drinks)."
              >
                Deactivate Drinks
              </button>
              <button
                type="button"
                className="btn btn-sm btn-menu-image"
                disabled={menuImageBusy}
                onClick={handleGenerateMenuImage}
                title="Download one flyer of today's active food menu for WhatsApp. Drinks are not included."
              >
                {menuImageBusy ? 'Generating…' : 'Generate Menu Image'}
              </button>
              <button className="btn btn-sm btn-primary" onClick={() => openEditItem({})}>
                + Add Item
              </button>
            </div>
          </div>

          <div className="admin-search-bar">
            <input
              type="text"
              placeholder="Search menu items..."
              value={menuSearch}
              onChange={(e) => setMenuSearch(e.target.value)}
              className="admin-search-input"
            />
            {menuSearch && (
              <button className="btn btn-sm btn-outline" onClick={() => setMenuSearch('')}>
                Clear
              </button>
            )}
          </div>

          <div className={`fasting-toggle-banner ${fastingMode ? 'fasting-on' : ''}`}>
            <div className="fasting-toggle-info">
              <strong>{fastingMode ? 'Fasting Mode ON' : 'Fasting Mode OFF'}</strong>
              <span>{fastingMode ? 'Non-fasting items hidden. Fasting and General items available.' : 'Fasting items hidden. Non-fasting and General items available.'}</span>
            </div>
            <button
              className={`btn btn-sm ${fastingMode ? 'btn-warning' : 'btn-outline'}`}
              onClick={async () => {
                const next = !fastingMode;
                if (!window.confirm(next
                  ? 'Turn on fasting mode? Non-fasting items will be hidden and fasting items will be shown.'
                  : 'Turn off fasting mode? Fasting items will be hidden and non-fasting items will be shown.'
                )) return;
                await api.toggleFasting(next);
                setFastingMode(next);
                refresh();
              }}
            >
              {fastingMode ? 'Turn Off' : 'Turn On'}
            </button>
          </div>

          {editingItem && (
            <form className="admin-form card" onSubmit={saveItem}>
              <h3>{editingItem.id ? 'Edit Item' : 'New Item'}</h3>
              <div className="form-row">
                <div className="form-group">
                  <label>Name</label>
                  <input name="name" defaultValue={editingItem.name || ''} required />
                </div>
                <div className="form-group">
                  <label>Price ($)</label>
                  <input name="price" type="number" step="0.01" min="0" defaultValue={editingItem.price || ''} />
                  <small className="admin-hint">For donations, this is only a suggested amount (can be 0).</small>
                </div>
              </div>
              <div className="form-group">
                <label>Description</label>
                <input name="description" defaultValue={editingItem.description || ''} />
              </div>
              <div className="form-row">
                <div className="form-group">
                  <label>Category</label>
                  <select name="category_id" defaultValue={editingItem.category_id || ''} required>
                    <option value="">Select...</option>
                    {activeCategories.map((c) => (
                      <option key={c.id} value={c.id}>
                        {c.name} ({VENUE_LABELS[c.venue] || c.venue})
                      </option>
                    ))}
                  </select>
                </div>
                <div className="form-group">
                  <label>Stock Qty (blank = unlimited)</label>
                  <input
                    name="stock_quantity"
                    type="text"
                    inputMode="numeric"
                    autoComplete="off"
                    defaultValue={editingItem.stock_quantity ?? ''}
                    placeholder="Unlimited"
                  />
                </div>
              </div>
              <div className="form-row">
                <div className="form-group">
                  <label>Max per account / day (blank = no limit)</label>
                  <input
                    name="max_per_user"
                    type="number"
                    min="1"
                    defaultValue={editingItem.max_per_user ?? ''}
                    placeholder="No limit"
                  />
                  <small style={{ color: 'var(--text-muted, #888)' }}>
                    Only enforced for logged-in customer accounts.
                  </small>
                </div>
              </div>
              <div className="form-group">
                <label>Item Photo</label>
                <div className="image-upload-area">
                  {editingItem.image_url && !imageFile && (
                    <div className="image-upload-preview">
                      <img src={mediaUrl(editingItem.image_url)} alt={editingItem.name} />
                      <button
                        type="button"
                        className="btn btn-sm btn-danger"
                        onClick={() => editingItem.id && handleDeleteImage(editingItem.id)}
                      >
                        Remove
                      </button>
                    </div>
                  )}
                  {imageFile && (
                    <div className="image-upload-preview">
                      <img src={URL.createObjectURL(imageFile)} alt="Preview" />
                      <button
                        type="button"
                        className="btn btn-sm btn-outline"
                        onClick={() => setImageFile(null)}
                      >
                        Clear
                      </button>
                    </div>
                  )}
                  <input
                    type="file"
                    accept="image/*"
                    onChange={(e) => setImageFile(e.target.files[0] || null)}
                  />
                </div>
              </div>
              <div className="form-row">
                <div className="form-group">
                  <label>Sort Order</label>
                  <input name="sort_order" type="number" defaultValue={editingItem.sort_order || 0} />
                </div>
                <div className="form-group">
                  <label>Diet Type</label>
                  <select name="diet_type" defaultValue={editingItem.diet_type || 'non_fasting'}>
                    <option value="non_fasting">Non-Fasting</option>
                    <option value="fasting">Fasting</option>
                    <option value="general">General (Always Available)</option>
                  </select>
                </div>
                <div className="form-group form-checkbox">
                  <label>
                    <input
                      name="is_adhoc"
                      type="checkbox"
                      defaultChecked={editingItem.is_adhoc === 1}
                    />
                    Ad-hoc item (not saved in templates)
                  </label>
                </div>
                <div className="form-group form-checkbox">
                  <label>
                    <input
                      name="variable_price"
                      type="checkbox"
                      defaultChecked={editingItem.variable_price === 1}
                    />
                    Variable amount (donation — staff/customer types the dollars)
                  </label>
                </div>
              </div>
              {modifierGroups.length > 0 && (
                <div className="form-group">
                  <label>Modifier Groups</label>
                  <div className="modifier-checkboxes">
                    {modifierGroups.map((g) => (
                      <label key={g.id} className="modifier-checkbox-label">
                        <input
                          type="checkbox"
                          checked={isModifierAssigned(g.id)}
                          onChange={(e) => toggleModifier(g.id, e.target.checked)}
                        />
                        {g.name} {g.required ? '(required)' : '(optional)'}
                      </label>
                    ))}
                  </div>
                  {itemModifiers.length > 1 && (
                    <div className="modifier-order-list">
                      <label>Display Order</label>
                      {[...itemModifiers].sort((a, b) => (a.sort_order || 0) - (b.sort_order || 0)).map((m, idx) => {
                        const g = modifierGroups.find((mg) => mg.id === m.modifier_group_id);
                        if (!g) return null;
                        return (
                          <div key={g.id} className="modifier-order-row">
                            <span className="modifier-order-pos">{idx + 1}.</span>
                            <span className="modifier-order-name">{g.name}</span>
                            <button type="button" className="btn btn-sm btn-outline modifier-order-btn" disabled={idx === 0} onClick={() => moveModifier(g.id, -1)}>&uarr;</button>
                            <button type="button" className="btn btn-sm btn-outline modifier-order-btn" disabled={idx === itemModifiers.length - 1} onClick={() => moveModifier(g.id, 1)}>&darr;</button>
                          </div>
                        );
                      })}
                    </div>
                  )}
                </div>
              )}
              <div className="form-actions">
                <button type="submit" className="btn btn-sm btn-primary" disabled={uploading}>
                  {uploading ? 'Uploading...' : 'Save'}
                </button>
                <button type="button" className="btn btn-sm btn-outline" onClick={() => { setEditingItem(null); setImageFile(null); setItemModifiers([]); }}>Cancel</button>
              </div>
            </form>
          )}

          <div className="admin-table-wrap">
            <table className="admin-table">
              <thead>
                <tr>
                  <th></th>
                  <th></th>
                  <th>Name</th>
                  <th>Category</th>
                  <th>Price</th>
                  <th>Stock</th>
                  <th>Status</th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {filteredItems.map((item, idx) => {
                  const stockGroups = item.stock_modifier_groups || [];
                  const hasOptionStock = stockGroups.length > 0;
                  const allOptions = stockGroups.flatMap((g) => g.options);
                  // A modifier option with no stock value = unlimited; that keeps
                  // the parent item orderable, so it should never count as sold out.
                  const hasUnlimitedOption = hasOptionStock && allOptions.some(
                    (o) => o.stock_quantity === null || o.stock_quantity === undefined
                  );
                  const optionTotal = hasOptionStock
                    ? allOptions.reduce((sum, o) => sum + (o.stock_quantity !== null && o.stock_quantity !== undefined ? o.stock_quantity : 0), 0)
                    : null;
                  const displayStock = hasOptionStock
                    ? (hasUnlimitedOption ? null : optionTotal)
                    : item.stock_quantity;
                  const soldOut = displayStock !== null && displayStock <= 0;
                  return (
                    <React.Fragment key={item.id}>
                      <tr
                        className={`${!item.active ? 'row-inactive' : ''} ${soldOut ? 'row-soldout' : ''} ${dragType === 'item' && dragIdx === idx ? 'dragging' : ''} ${dragType === 'item' && dragOverIdx === idx ? 'drag-over' : ''}`}
                        onDragOver={(e) => handleDragOver(e, idx)}
                        onDrop={() => handleDrop('item', filteredItems, idx)}
                      >
                        <td
                          className="drag-handle"
                          title="Drag to reorder"
                          draggable
                          onDragStart={() => handleDragStart('item', idx)}
                          onDragEnd={handleDragEnd}
                        >
                          &#9776;
                        </td>
                        <td className="thumb-cell">
                          {item.image_url ? (
                            <img src={mediaUrl(item.image_url)} alt="" className="admin-thumb" />
                          ) : (
                            <span className="admin-thumb-empty">No img</span>
                          )}
                        </td>
                        <td>
                          <strong>{item.name}</strong>
                          {item.is_adhoc === 1 && <span className="adhoc-badge">Ad-hoc</span>}
                          {item.variable_price === 1 && <span className="adhoc-badge">Donation</span>}
                          <span className={`diet-badge diet-${item.diet_type || 'non_fasting'}`}>
                            {item.diet_type === 'fasting' ? 'Fasting' : item.diet_type === 'general' ? 'General' : 'Non-Fasting'}
                          </span>
                        </td>
                        <td>
                          <span className="venue-label">{VENUE_LABELS[item.venue] || item.venue}</span>
                          <br />{item.category_name}
                        </td>
                        <td>${Number(item.price || 0).toFixed(2)}</td>
                        <td>
                          {hasOptionStock ? (
                            <span className="stock-total-display">
                              {hasUnlimitedOption ? 'Unlimited' : optionTotal}
                            </span>
                          ) : (
                            <StockQtyInput
                              value={item.stock_quantity}
                              onCommit={(qty) => commitItemStock(item.id, qty)}
                            />
                          )}
                        </td>
                        <td>
                          {soldOut && Number(item.active) === 1 && (
                            <span className="badge badge-cancelled" style={{ marginRight: 6 }}>Sold Out</span>
                          )}
                          <button
                            type="button"
                            className={`btn btn-sm ${Number(item.active) === 1 ? 'btn-success' : 'btn-danger'}`}
                            onClick={(e) => { e.stopPropagation(); toggleItemActive(item); }}
                            title={soldOut ? 'Item is active/inactive independently of stock. Set stock above 0 (or clear for unlimited) to sell it.' : undefined}
                          >
                            {Number(item.active) === 1 ? 'Active' : 'Inactive'}
                          </button>
                        </td>
                        <td className="actions-cell">
                          <button type="button" className="btn btn-sm btn-outline" onClick={(e) => { e.stopPropagation(); openEditItem(item); }}>Edit</button>
                          <button type="button" className="btn btn-sm btn-danger" onClick={(e) => { e.stopPropagation(); handleDeleteItem(item); }}>Delete</button>
                        </td>
                      </tr>
                      {stockGroups.map((group) =>
                        group.options.map((opt) => (
                          <tr key={`opt-${opt.id}`} className="option-stock-row">
                            <td></td>
                            <td></td>
                            <td colSpan={3} className="option-stock-label">
                              <span className="option-stock-indent">↳</span> {group.name}: <strong>{opt.name}</strong>
                              {opt.surcharge > 0 ? ` (+$${opt.surcharge.toFixed(2)})` : ''}
                            </td>
                            <td>
                              <StockQtyInput
                                value={opt.stock_quantity}
                                onCommit={(qty) => commitOptionStock(opt.id, qty)}
                              />
                            </td>
                            <td>
                              {opt.stock_quantity !== null && opt.stock_quantity <= 0 ? (
                                <span className="badge badge-cancelled">Sold Out</span>
                              ) : null}
                            </td>
                            <td></td>
                          </tr>
                        ))
                      )}
                    </React.Fragment>
                  );
                })}
              </tbody>
            </table>
          </div>
        </section>
      )}

      {tab === 'categories' && (
        <section className="admin-section">
          <div className="admin-section-header">
            <h2>Categories</h2>
            <button className="btn btn-sm btn-primary" onClick={() => setEditingCat({})}>
              + Add Category
            </button>
          </div>

          {editingCat && (
            <form className="admin-form card" onSubmit={saveCat}>
              <h3>{editingCat.id ? 'Edit Category' : 'New Category'}</h3>
              <div className="form-row">
                <div className="form-group">
                  <label>Name</label>
                  <input name="name" defaultValue={editingCat.name || ''} required />
                </div>
                <div className="form-group">
                  <label>Venue</label>
                  <select name="venue" defaultValue={editingCat.venue || 'kitchen'}>
                    <option value="kitchen">Kitchen</option>
                    <option value="cafe">Holy Ground Cafe</option>
                    <option value="desserts">Desserts</option>
                  </select>
                </div>
              </div>
              <div className="form-row">
                <div className="form-group">
                  <label>Physical Kitchen</label>
                  <select name="kitchen" defaultValue={editingCat.kitchen || ''}>
                    <option value="">None (Cafe / Desserts / General)</option>
                    <option value="st_noufer">St Noufer's Kitchen</option>
                    <option value="st_barbara">St Barbara's Kitchen</option>
                    <option value="turkish_coffee">Turkish Coffee stand</option>
                  </select>
                </div>
                <div className="form-group">
                  <label>Sort Order</label>
                  <input name="sort_order" type="number" defaultValue={editingCat.sort_order || 0} />
                </div>
              </div>
              <div className="form-row">
                <div className="form-group form-checkbox">
                  <label>
                    <input
                      name="is_drinks"
                      type="checkbox"
                      defaultChecked={editingCat.is_drinks === 1}
                    />
                    Drinks category? (Cafe only — coffee, tea, cold drinks)
                  </label>
                  <small style={{ color: 'var(--text-muted, #888)' }}>
                    Tick this so the "Deactivate Drinks" button targets it, and cafe food stays on the "Deactivate Food" button.
                  </small>
                </div>
              </div>
              <div className="form-row">
                <div className="form-group form-checkbox">
                  <label>
                    <input
                      name="is_hot_drink"
                      type="checkbox"
                      defaultChecked={editingCat.is_hot_drink === 1}
                    />
                    Route to hot-drinks sticker printer
                  </label>
                  <small style={{ color: 'var(--text-muted, #888)' }}>
                    Only used when this is a cafe drinks category. Ticked = stickers print on the espresso-side Zebra; unticked = stickers print on the cold/cafe-food Zebra.
                  </small>
                </div>
              </div>
              <div className="form-row">
                <div className="form-group form-checkbox">
                  <label>
                    <input
                      name="collected_at_counter"
                      type="checkbox"
                      defaultChecked={editingCat.collected_at_counter === 1}
                    />
                    Collected at the counter (POS only — hide on the app, no kitchen docket or sticker)
                  </label>
                  <small style={{ color: 'var(--text-muted, #888)' }}>
                    Use for Orban / donations taken at the counter. Items stay on the POS and in reports.
                  </small>
                </div>
              </div>
              <div className="form-actions">
                <button type="submit" className="btn btn-sm btn-primary">Save</button>
                <button type="button" className="btn btn-sm btn-outline" onClick={() => setEditingCat(null)}>Cancel</button>
              </div>
            </form>
          )}

          <div className="admin-table-wrap">
            <table className="admin-table">
              <thead>
                <tr>
                  <th></th>
                  <th>Name</th>
                  <th>Venue</th>
                  <th>Kitchen</th>
                  <th>Status</th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {filteredCategories.map((cat, idx) => (
                  <tr
                    key={cat.id}
                    className={`${!cat.active ? 'row-inactive' : ''} ${dragType === 'category' && dragIdx === idx ? 'dragging' : ''} ${dragType === 'category' && dragOverIdx === idx ? 'drag-over' : ''}`}
                    onDragOver={(e) => handleDragOver(e, idx)}
                    onDrop={() => handleDrop('category', filteredCategories, idx)}
                  >
                    <td
                      className="drag-handle"
                      title="Drag to reorder"
                      draggable
                      onDragStart={() => handleDragStart('category', idx)}
                      onDragEnd={handleDragEnd}
                    >
                      &#9776;
                    </td>
                    <td>
                      <strong>{cat.name}</strong>
                      {cat.is_drinks === 1 && (
                        <span className="drinks-badge" title="Deactivate Drinks targets this category">Drinks</span>
                      )}
                      {cat.is_hot_drink === 1 && (
                        <span
                          className="drinks-badge"
                          style={{ background: '#e28b3a', color: '#fff' }}
                          title="Stickers for this category print on the hot-drinks Zebra"
                        >
                          Hot
                        </span>
                      )}
                      {cat.collected_at_counter === 1 && (
                        <span
                          className="adhoc-badge"
                          title="POS only — not on the app, no kitchen docket"
                        >
                          Counter
                        </span>
                      )}
                    </td>
                    <td>{VENUE_LABELS[cat.venue] || cat.venue}</td>
                    <td>{cat.kitchen ? (KITCHEN_LABELS[cat.kitchen] || cat.kitchen) : '—'}</td>
                    <td>
                      <button
                        type="button"
                        className={`btn btn-sm ${cat.active ? 'btn-success' : 'btn-danger'}`}
                        onClick={(e) => { e.stopPropagation(); toggleCatActive(cat); }}
                      >
                        {cat.active ? 'Active' : 'Inactive'}
                      </button>
                    </td>
                    <td className="actions-cell">
                      <button type="button" className="btn btn-sm btn-outline" onClick={(e) => { e.stopPropagation(); setEditingCat(cat); }}>Edit</button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </section>
      )}

      {tab === 'modifiers' && (
        <section className="admin-section">
          <div className="admin-section-header">
            <h2>Modifier Groups</h2>
            <button className="btn btn-sm btn-primary" onClick={() => setEditingModGroup({ name: '', required: true, sort_order: 0, track_stock: false })}>
              + Add Group
            </button>
          </div>
          <p className="admin-hint">
            Create modifier groups (e.g. Milk, Size, Syrup) and their options with surcharges. Then assign groups to individual menu items via the item edit form.
          </p>

          {editingModGroup && (() => {
            // Normalise the editing-state hide-rule to an array of numeric ids
            // regardless of whether the server returned a JSON string, a real
            // array, or null. This keeps the multi-select wired to a stable
            // shape while the admin clicks through it.
            const parseIds = (raw) => {
              if (raw === null || raw === undefined || raw === '') return [];
              let arr = raw;
              if (typeof arr === 'string') {
                try { arr = JSON.parse(arr); } catch { return []; }
              }
              if (!Array.isArray(arr)) return [];
              return arr.map((x) => Number(x)).filter((x) => Number.isInteger(x) && x > 0);
            };
            const hiddenWhenIds = parseIds(editingModGroup.hidden_when_option_ids);
            const triggerCandidates = (modifierGroups || [])
              .filter((g) => g.id !== editingModGroup.id) // can't hide based on own options
              .flatMap((g) => (g.options || []).map((opt) => ({
                id: opt.id,
                label: `${g.name} \u2192 ${opt.name}`,
              })));
            const toggleHiddenId = (optId, checked) => {
              setEditingModGroup((p) => {
                const current = parseIds(p.hidden_when_option_ids);
                const next = checked
                  ? Array.from(new Set([...current, optId]))
                  : current.filter((x) => x !== optId);
                return { ...p, hidden_when_option_ids: next };
              });
            };

            return (
            <form key={editingModGroup.id || 'new'} className="admin-form card" onSubmit={async (e) => {
              e.preventDefault();
              const data = {
                name: editingModGroup.name,
                required: !!editingModGroup.required,
                sort_order: editingModGroup.sort_order || 0,
                track_stock: !!editingModGroup.track_stock,
                hidden_when_option_ids: hiddenWhenIds.length > 0 ? hiddenWhenIds : null,
              };
              if (editingModGroup.id) {
                await api.updateModifierGroup(editingModGroup.id, data);
              } else {
                await api.createModifierGroup(data);
              }
              setEditingModGroup(null);
              api.getModifierGroups().then(setModifierGroups);
            }}>
              <h3>{editingModGroup.id ? 'Edit Group' : 'New Modifier Group'}</h3>
              <div className="form-row">
                <div className="form-group">
                  <label>Name</label>
                  <input
                    value={editingModGroup.name || ''}
                    onChange={(e) => setEditingModGroup((p) => ({ ...p, name: e.target.value }))}
                    required
                  />
                </div>
                <div className="form-group">
                  <label>Sort Order</label>
                  <input
                    type="number"
                    value={editingModGroup.sort_order || 0}
                    onChange={(e) => setEditingModGroup((p) => ({ ...p, sort_order: Number(e.target.value) }))}
                  />
                </div>
              </div>
              <div className="form-group form-checkbox">
                <label>
                  <input
                    type="checkbox"
                    checked={!!editingModGroup.required}
                    onChange={(e) => setEditingModGroup((p) => ({ ...p, required: e.target.checked }))}
                  />
                  Required selection
                </label>
              </div>
              <div className="form-group form-checkbox">
                <label>
                  <input
                    type="checkbox"
                    checked={!!editingModGroup.track_stock}
                    onChange={(e) => setEditingModGroup((p) => ({ ...p, track_stock: e.target.checked }))}
                  />
                  Track stock for options in this group
                </label>
              </div>
              {triggerCandidates.length > 0 && (
                <div className="form-group">
                  <label>Hide this group when any of these options is selected</label>
                  <p className="admin-hint" style={{ marginTop: 0 }}>
                    Tick options from <em>other</em> groups that should make this group disappear.
                    Example: tick <code>Type &rarr; Long Black</code> and <code>Type &rarr; Espresso</code>
                    on a "Milk" group so customers ordering those coffees skip the milk picker.
                    Leave all unticked for the historical "always show" behaviour.
                  </p>
                  <div className="modifier-checkboxes">
                    {triggerCandidates.map((opt) => (
                      <label key={opt.id} className="modifier-checkbox-label">
                        <input
                          type="checkbox"
                          checked={hiddenWhenIds.includes(opt.id)}
                          onChange={(e) => toggleHiddenId(opt.id, e.target.checked)}
                        />
                        {opt.label}
                      </label>
                    ))}
                  </div>
                </div>
              )}
              <div className="form-actions">
                <button type="submit" className="btn btn-sm btn-primary">Save</button>
                <button type="button" className="btn btn-sm btn-outline" onClick={() => setEditingModGroup(null)}>Cancel</button>
              </div>
            </form>
            );
          })()}

          {modifierGroups.map((group) => (
            <div key={group.id} className="modifier-group-card card">
              <div className="modifier-group-header">
                <div>
                  <strong>{group.name}</strong>
                  <span className={`modifier-req-badge ${group.required ? 'required' : 'optional'}`}>
                    {group.required ? 'Required' : 'Optional'}
                  </span>
                  {group.track_stock ? (
                    <span className="modifier-req-badge stock-tracked">Tracks Stock</span>
                  ) : null}
                </div>
                <div className="actions-cell">
                  <button className="btn btn-sm btn-outline" onClick={() => setEditingModGroup(group)}>Edit</button>
                  <button className="btn btn-sm btn-danger" onClick={async () => {
                    if (!window.confirm(`Delete "${group.name}" and all its options?`)) return;
                    await api.deleteModifierGroup(group.id);
                    api.getModifierGroups().then(setModifierGroups);
                  }}>Delete</button>
                </div>
              </div>

              <table className="admin-table modifier-options-table">
                <thead>
                  <tr>
                    <th>Option</th>
                    <th>Surcharge</th>
                    <th>Order</th>
                    <th>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {(group.options || []).map((opt) => (
                    <tr key={opt.id}>
                      {editingModOption?.id === opt.id ? (
                        <>
                          <td>
                            <input
                              className="mod-inline-input"
                              value={editingModOption.name}
                              onChange={(e) => setEditingModOption((p) => ({ ...p, name: e.target.value }))}
                            />
                          </td>
                          <td>
                            <input
                              className="mod-inline-input mod-price-input"
                              type="number"
                              step="0.01"
                              min="0"
                              value={editingModOption.surcharge}
                              onChange={(e) => setEditingModOption((p) => ({ ...p, surcharge: parseFloat(e.target.value) || 0 }))}
                            />
                          </td>
                          <td>
                            <input
                              className="mod-inline-input mod-order-input"
                              type="number"
                              value={editingModOption.sort_order}
                              onChange={(e) => setEditingModOption((p) => ({ ...p, sort_order: Number(e.target.value) }))}
                            />
                          </td>
                          <td className="actions-cell">
                            <button className="btn btn-sm btn-primary" onClick={async () => {
                              await api.updateModifierOption(editingModOption.id, editingModOption);
                              setEditingModOption(null);
                              api.getModifierGroups().then(setModifierGroups);
                            }}>Save</button>
                            <button className="btn btn-sm btn-outline" onClick={() => setEditingModOption(null)}>Cancel</button>
                          </td>
                        </>
                      ) : (
                        <>
                          <td>{opt.name}</td>
                          <td>{opt.surcharge > 0 ? `+$${opt.surcharge.toFixed(2)}` : 'Free'}</td>
                          <td>{opt.sort_order}</td>
                          <td className="actions-cell">
                            <button className="btn btn-sm btn-outline" onClick={() => setEditingModOption({ ...opt })}>Edit</button>
                            <button className="btn btn-sm btn-danger" onClick={async () => {
                              if (!window.confirm(`Delete option "${opt.name}"?`)) return;
                              await api.deleteModifierOption(opt.id);
                              api.getModifierGroups().then(setModifierGroups);
                            }}>Delete</button>
                          </td>
                        </>
                      )}
                    </tr>
                  ))}
                  {newOptionGroupId === group.id && (
                    <tr>
                      <td colSpan={4}>
                        <form className="new-option-inline" onSubmit={async (e) => {
                          e.preventDefault();
                          const form = new FormData(e.target);
                          await api.addModifierOption(group.id, {
                            name: form.get('name'),
                            surcharge: parseFloat(form.get('surcharge')) || 0,
                            sort_order: Number(form.get('sort_order') || 0),
                          });
                          setNewOptionGroupId(null);
                          api.getModifierGroups().then(setModifierGroups);
                        }}>
                          <input name="name" placeholder="Option name" required />
                          <input name="surcharge" type="number" step="0.01" min="0" placeholder="Surcharge" defaultValue="0" />
                          <input name="sort_order" type="number" placeholder="Order" defaultValue="0" style={{ width: 60 }} />
                          <button type="submit" className="btn btn-sm btn-primary">Add</button>
                          <button type="button" className="btn btn-sm btn-outline" onClick={() => setNewOptionGroupId(null)}>Cancel</button>
                        </form>
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
              {newOptionGroupId !== group.id && (
                <button className="btn btn-sm btn-outline modifier-add-option-btn" onClick={() => setNewOptionGroupId(group.id)}>
                  + Add Option
                </button>
              )}
            </div>
          ))}
        </section>
      )}

      {tab === 'templates' && (
        <section className="admin-section">
          <h2>Menu Templates</h2>
          <p className="admin-hint">
            Save the current standard menu as a template. Load it each week to reset stock and menu items. Ad-hoc items are not included in templates.
          </p>

          <div className="template-save card">
            <h3>Save Current Menu as Template</h3>
            <div className="template-save-row">
              <input
                type="text"
                placeholder="Template name (e.g. Standard Sunday Menu)"
                value={templateName}
                onChange={(e) => setTemplateName(e.target.value)}
              />
              <button className="btn btn-sm btn-primary" onClick={handleSaveTemplate} disabled={!templateName.trim()}>
                Save Template
              </button>
            </div>
          </div>

          {templates.length === 0 ? (
            <p style={{ color: 'var(--gray-400)', padding: '20px 0' }}>No templates saved yet</p>
          ) : (
            <div className="admin-table-wrap">
              <table className="admin-table">
                <thead>
                  <tr>
                    <th>Name</th>
                    <th>Created</th>
                    <th>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {templates.map((t) => (
                    <tr key={t.id}>
                      <td><strong>{t.name}</strong></td>
                      <td>{fmtAUDate(new Date(t.created_at))}</td>
                      <td className="actions-cell">
                        <button className="btn btn-sm btn-primary" onClick={() => handleLoadTemplate(t.id)}>Load</button>
                        <button className="btn btn-sm btn-danger" onClick={() => handleDeleteTemplate(t.id)}>Delete</button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </section>
      )}

      {tab === 'accounts' && isSuperAdmin && (
        <section className="admin-section">
          <div className="admin-section-header">
            <h2>Signed-up accounts</h2>
            <div className="admin-section-actions" style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
              <button
                type="button"
                className="btn btn-sm btn-outline"
                disabled={accountsExporting}
                onClick={() => {
                  setAccountsExporting(true);
                  api
                    .exportAdminUsersCsv()
                    .catch((e) => alert('Export failed: ' + e.message))
                    .finally(() => setAccountsExporting(false));
                }}
              >
                {accountsExporting ? 'Exporting…' : 'Export CSV'}
              </button>
              <button
                type="button"
                className="btn btn-sm btn-outline"
                disabled={accountsLoading}
                onClick={() => {
                  setAccountsLoading(true);
                  api
                    .getAdminUsers()
                    .then(setAccounts)
                    .catch((e) => alert('Failed to refresh: ' + e.message))
                    .finally(() => setAccountsLoading(false));
                }}
              >
                {accountsLoading ? 'Loading…' : 'Refresh'}
              </button>
            </div>
          </div>
          <p className="admin-hint">
            Customer accounts created via sign-up. Passwords are never shown here.
            Sunday School servant requests from registration appear as <strong>Pending</strong> —
            approve them so cafe stickers print a bold SUNDAY SCHOOL banner, or reject to clear the request.
            Use <strong>Export CSV</strong> to download every account (ignores the search box).
          </p>

          <div className="admin-search-bar">
            <input
              type="text"
              placeholder="Search by name or email..."
              value={accountSearch}
              onChange={(e) => setAccountSearch(e.target.value)}
              className="admin-search-input"
            />
            {accountSearch && (
              <button className="btn btn-sm btn-outline" onClick={() => setAccountSearch('')}>
                Clear
              </button>
            )}
          </div>

          {accountsLoading && accounts.length === 0 ? (
            <p style={{ color: 'var(--gray-400)', padding: '20px 0' }}>Loading…</p>
          ) : accounts.length === 0 ? (
            <p style={{ color: 'var(--gray-400)', padding: '20px 0' }}>No accounts yet</p>
          ) : (
            <div className="admin-accounts-list">
              {filteredAccounts.map((u) => {
                const ss = u.sunday_school_status || 'none';
                return (
                  <button
                    key={u.id}
                    type="button"
                    className={`admin-account-row${ss === 'pending' ? ' is-pending' : ''}${ss === 'approved' ? ' is-approved' : ''}`}
                    onClick={() => setAccountDetail(u)}
                  >
                    <div className="admin-account-main">
                      <strong className="admin-account-name">{u.name}</strong>
                      <span className="admin-account-email">{u.email}</span>
                    </div>
                    <div className="admin-account-meta">
                      {ss === 'approved' && <span className="admin-ss-badge admin-ss-approved">Sunday School</span>}
                      {ss === 'pending' && <span className="admin-ss-badge admin-ss-pending">Pending</span>}
                      {!u.email_verified && <span className="admin-ss-badge admin-ss-unverified">Unverified</span>}
                      <span className="admin-account-orders">{u.order_count ?? 0} orders</span>
                      {Number(u.saved_card_count) > 0 && (
                        <span className="admin-account-orders">
                          {u.saved_card_count} saved card{Number(u.saved_card_count) === 1 ? '' : 's'}
                        </span>
                      )}
                      <span className="admin-account-open">Manage</span>
                    </div>
                  </button>
                );
              })}
            </div>
          )}

          {accountDetail && (() => {
            const u = accountDetail;
            const ss = u.sunday_school_status || 'none';
            return (
              <div className="admin-modal-overlay" onClick={() => setAccountDetail(null)}>
                <div className="admin-modal admin-account-modal card" onClick={(e) => e.stopPropagation()}>
                  <div className="admin-modal-header">
                    <div>
                      <h3>{u.name}</h3>
                      <p className="admin-account-modal-email">{u.email}</p>
                    </div>
                    <button type="button" className="btn btn-sm btn-outline" onClick={() => setAccountDetail(null)}>Close</button>
                  </div>

                  <dl className="admin-account-details">
                    <div>
                      <dt>Email verified</dt>
                      <dd>{u.email_verified ? 'Yes' : 'No'}</dd>
                    </div>
                    <div>
                      <dt>Sunday School</dt>
                      <dd>
                        {ss === 'approved' && <span className="admin-ss-badge admin-ss-approved">Approved</span>}
                        {ss === 'pending' && <span className="admin-ss-badge admin-ss-pending">Pending approval</span>}
                        {ss === 'none' && 'Not a Sunday School servant'}
                      </dd>
                    </div>
                    <div>
                      <dt>Orders</dt>
                      <dd>{u.order_count ?? 0}</dd>
                    </div>
                    <div>
                      <dt>Saved cards</dt>
                      <dd>{u.saved_card_count ?? 0}</dd>
                    </div>
                    <div>
                      <dt>Last order</dt>
                      <dd>{fmtAU(parseDbDateTime(u.last_order_at))}</dd>
                    </div>
                    <div>
                      <dt>Signed up</dt>
                      <dd>{fmtAU(parseDbDateTime(u.created_at))}</dd>
                    </div>
                  </dl>

                  <div className="admin-account-modal-actions">
                    <button type="button" className="btn btn-sm btn-outline" onClick={() => handleViewUserOrders(u)}>
                      View orders
                    </button>
                    {!u.email_verified && (
                      <button type="button" className="btn btn-sm btn-success" onClick={() => handleVerifyUser(u)}>
                        Verify email
                      </button>
                    )}
                    {ss === 'pending' && (
                      <>
                        <button
                          type="button"
                          className="btn btn-sm btn-primary"
                          onClick={() => handleSundaySchoolStatus(u, 'approved')}
                        >
                          Approve Sunday School
                        </button>
                        <button
                          type="button"
                          className="btn btn-sm btn-outline"
                          onClick={() => handleSundaySchoolStatus(u, 'none', 'reject')}
                        >
                          Reject request
                        </button>
                      </>
                    )}
                    {ss === 'none' && (
                      <button
                        type="button"
                        className="btn btn-sm btn-primary"
                        onClick={() => handleSundaySchoolStatus(u, 'approved')}
                      >
                        Approve Sunday School
                      </button>
                    )}
                    {ss === 'approved' && (
                      <button
                        type="button"
                        className="btn btn-sm btn-outline"
                        onClick={() => handleSundaySchoolStatus(u, 'none')}
                      >
                        Revoke Sunday School
                      </button>
                    )}
                    <button type="button" className="btn btn-sm btn-danger" onClick={() => handleDeleteUser(u)}>
                      Delete account
                    </button>
                  </div>
                </div>
              </div>
            );
          })()}

          {selectedUser && (
            <div className="admin-modal-overlay" onClick={() => setSelectedUser(null)}>
              <div className="admin-modal card" onClick={(e) => e.stopPropagation()}>
                <div className="admin-modal-header">
                  <div>
                    <h3>{selectedUser.name}</h3>
                    <p style={{ color: 'var(--gray-500)', fontSize: 14, margin: 0 }}>{selectedUser.email}</p>
                  </div>
                  <button className="btn btn-sm btn-outline" onClick={() => setSelectedUser(null)}>Close</button>
                </div>
                {userOrdersLoading ? (
                  <p style={{ padding: '20px 0', textAlign: 'center', color: 'var(--gray-400)' }}>Loading orders…</p>
                ) : userOrders.length === 0 ? (
                  <p style={{ padding: '20px 0', textAlign: 'center', color: 'var(--gray-400)' }}>No orders found</p>
                ) : (
                  <div className="admin-table-wrap">
                    <table className="admin-table">
                      <thead>
                        <tr>
                          <th>Order #</th>
                          <th>Date</th>
                          <th>Items</th>
                          <th>Total</th>
                          <th>Status</th>
                        </tr>
                      </thead>
                      <tbody>
                        {userOrders.map((o) => (
                          <tr key={o.id}>
                            <td><strong>{o.order_number}</strong></td>
                            <td>{fmtAU(parseDbDateTime(o.created_at))}</td>
                            <td>{(o.items || []).map((i) => `${i.quantity}x ${i.item_name}`).join(', ')}</td>
                            <td>${o.total.toFixed(2)}</td>
                            <td>
                              <span className={`badge badge-${o.status}`}>{o.status}</span>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}
              </div>
            </div>
          )}
        </section>
      )}



      {tab === 'reports' && (
        <section className="admin-section">
          <h2>Sales Report</h2>
          <div className="report-filters card">
            <div className="form-row">
              <div className="form-group">
                <label>From</label>
                <input type="date" value={reportFrom} onChange={(e) => setReportFrom(e.target.value)} />
              </div>
              <div className="form-group">
                <label>To</label>
                <input type="date" value={reportTo} onChange={(e) => setReportTo(e.target.value)} />
              </div>
            </div>
            <div className="form-group">
              <label>Include in report</label>
              <div className="report-chip-row">
                {REPORT_GROUPS.map((g) => {
                  const included = !reportExclude.has(g.key);
                  return (
                    <button
                      key={g.key}
                      type="button"
                      className={`btn btn-sm ${included ? 'btn-primary' : 'btn-outline'}`}
                      onClick={() => toggleReportGroup(g.key)}
                      title={included ? `Included — click to exclude ${g.label}` : `Excluded — click to include ${g.label}`}
                    >
                      {included ? '\u2713 ' : ''}{g.label}
                    </button>
                  );
                })}
                <span className="report-chip-divider" aria-hidden="true">|</span>
                <button
                  type="button"
                  className="btn btn-sm btn-outline"
                  onClick={() => setReportExclude(new Set())}
                  title="Include every group"
                >
                  All
                </button>
                <button
                  type="button"
                  className="btn btn-sm btn-outline"
                  onClick={() => setReportExclude(new Set(REPORT_GROUPS.map((g) => g.key)))}
                  title="Exclude every group"
                >
                  None
                </button>
              </div>
            </div>
            <div className="form-actions">
              <button className="btn btn-sm btn-primary" onClick={loadReport}>Generate Report</button>
              <button className="btn btn-sm btn-outline" onClick={() => {
                const d = new Date();
                const today = `${d.getFullYear()}-${String(d.getMonth()+1).padStart(2,'0')}-${String(d.getDate()).padStart(2,'0')}`;
                setReportFrom(today);
                setReportTo(today);
              }}>Today</button>
            </div>
          </div>

          {report && (
            <>
              <div className="admin-stats report-summary">
                <div className="stat-card card">
                  <span className="stat-value">{report.summary.total_orders}</span>
                  <span className="stat-label">Total Orders</span>
                </div>
                <div className="stat-card card">
                  <span className="stat-value">${report.summary.total_revenue.toFixed(2)}</span>
                  <span className="stat-label">Total Revenue</span>
                </div>
                <div className="stat-card card">
                  <span className="stat-value">{report.summary.days_covered}</span>
                  <span className="stat-label">{report.summary.days_covered === 1 ? 'Day' : 'Days'}</span>
                </div>
              </div>
              {report.filter?.active && (
                <p className="report-filter-note">
                  Numbers exclude:{' '}
                  {REPORT_GROUPS
                    .filter((g) => !report.filter.included_groups.includes(g.key))
                    .map((g) => g.label)
                    .join(', ')}
                  . Revenue is summed from matching items only, so cross-group orders don't inflate totals.
                </p>
              )}

              {/* Channel breakdown — online (customer app/website) vs counter
                  (in-store POS). Always both panels visible so the split is
                  obvious even when one channel had zero sales in the period. */}
              {report.summary.by_channel && (
                <div className="report-channel-breakdown card">
                  <h3>Sales by Channel</h3>
                  <div className="report-channel-grid">
                    <div className="report-channel-cell report-channel-online">
                      <div className="report-channel-label">Online</div>
                      <div className="report-channel-value">
                        ${Number(report.summary.by_channel.online.revenue || 0).toFixed(2)}
                      </div>
                      <div className="report-channel-sub">
                        {report.summary.by_channel.online.orders || 0} orders
                        {report.summary.total_revenue > 0 && (
                          <> &middot; {Math.round(
                            (Number(report.summary.by_channel.online.revenue || 0) /
                              Number(report.summary.total_revenue || 1)) * 100
                          )}%</>
                        )}
                      </div>
                    </div>
                    <div className="report-channel-cell report-channel-counter">
                      <div className="report-channel-label">Counter</div>
                      <div className="report-channel-value">
                        ${Number(report.summary.by_channel.counter.revenue || 0).toFixed(2)}
                      </div>
                      <div className="report-channel-sub">
                        {report.summary.by_channel.counter.orders || 0} orders
                        {report.summary.total_revenue > 0 && (
                          <> &middot; {Math.round(
                            (Number(report.summary.by_channel.counter.revenue || 0) /
                              Number(report.summary.total_revenue || 1)) * 100
                          )}%</>
                        )}
                      </div>
                    </div>
                  </div>
                </div>
              )}

              {/* Per-station panel — counter iPads only. Suppressed when no
                  counter sales in the period (no station orders → no rows). */}
              {Array.isArray(report.by_station) && report.by_station.length > 0 && (
                <div className="report-station-breakdown card">
                  <h3>Counter Sales by Station</h3>
                  <div className="report-station-list">
                    {report.by_station.map((s) => (
                      <div key={s.station_id || 'unknown'} className="report-station-row">
                        <span className="report-station-name">{s.station_name || 'Unknown station'}</span>
                        <span className="report-station-orders">{s.orders} orders</span>
                        <span className="report-station-revenue">${Number(s.revenue || 0).toFixed(2)}</span>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {report.payment_breakdown.length > 0 && (
                <div className="report-payment-breakdown card">
                  <h3>Payment Breakdown</h3>
                  {report.payment_breakdown.map((p, i) => (
                    <div key={`${p.channel}-${p.method}-${i}`} className="report-payment-row">
                      <span>
                        {p.channel === 'counter' ? (
                          <span className="badge badge-counter" style={{ marginRight: 6 }}>Counter</span>
                        ) : (
                          <span className="badge badge-online" style={{ marginRight: 6 }}>Online</span>
                        )}
                        {p.method}
                      </span>
                      <span>{p.count} orders</span>
                      <span className="report-payment-amount">${p.revenue.toFixed(2)}</span>
                    </div>
                  ))}
                </div>
              )}

              <div className="admin-table-wrap">
                <table className="admin-table">
                  <thead>
                    <tr>
                      <th>Product</th>
                      <th>Category</th>
                      <th>Qty Sold</th>
                      <th>Revenue</th>
                    </tr>
                  </thead>
                  <tbody>
                    {report.products.length === 0 ? (
                      <tr><td colSpan={4} style={{ textAlign: 'center', color: 'var(--gray-400)' }}>No sales in this period</td></tr>
                    ) : (
                      report.products.flatMap((p, i) => {
                        // Show per-variant rows when the product was sold with any
                        // modifier choice (e.g. Grilled vs Fried, pizza size/toppings),
                        // or whenever it has more than one variant entry. A plain
                        // no-modifier product collapses back to a single line.
                        const variants = Array.isArray(p.variants) ? p.variants : [];
                        const showVariants = variants.length > 1
                          || variants.some((v) => v.options_label);
                        // Channel split under the totals — small text that
                        // shows how much of the qty/revenue came from Online
                        // vs Counter. Hidden when one channel is zero (no
                        // noise on products that only sell in one channel).
                        const hasOnline = Number(p.online_qty) > 0;
                        const hasCounter = Number(p.counter_qty) > 0;
                        const showSplit = hasOnline && hasCounter;
                        const rows = [
                          <tr key={`p-${i}`}>
                            <td><strong>{p.item_name}</strong></td>
                            <td>
                              {p.category_name}
                              {p.kitchen && <span className="report-kitchen-tag" style={{ color: p.kitchen === 'st_noufer' ? '#1e6091' : p.kitchen === 'turkish_coffee' ? '#6f4e37' : '#b5446e' }}> ({KITCHEN_LABELS[p.kitchen] || p.kitchen})</span>}
                            </td>
                            <td>
                              <strong>{p.total_qty}</strong>
                              {showSplit && (
                                <div className="report-channel-mini" style={{ fontSize: 11, color: 'var(--gray-500)', marginTop: 2 }}>
                                  <span title="Online (customer app / website)">{p.online_qty} online</span>
                                  {' \u00B7 '}
                                  <span title="Counter (in-store POS)">{p.counter_qty} counter</span>
                                </div>
                              )}
                            </td>
                            <td>
                              <strong>${p.total_revenue.toFixed(2)}</strong>
                              {showSplit && (
                                <div className="report-channel-mini" style={{ fontSize: 11, color: 'var(--gray-500)', marginTop: 2 }}>
                                  <span>${Number(p.online_revenue || 0).toFixed(2)} online</span>
                                  {' \u00B7 '}
                                  <span>${Number(p.counter_revenue || 0).toFixed(2)} counter</span>
                                </div>
                              )}
                            </td>
                          </tr>,
                        ];
                        if (showVariants) {
                          for (let vi = 0; vi < variants.length; vi++) {
                            const v = variants[vi];
                            const pct = p.total_qty > 0
                              ? Math.round((v.total_qty / p.total_qty) * 100)
                              : 0;
                            rows.push(
                              <tr key={`p-${i}-v-${vi}`} className="report-variant-row">
                                <td style={{ paddingLeft: '32px', color: 'var(--gray-600)' }}>
                                  &#8627; {v.options_label || <em>No modifier</em>}
                                </td>
                                <td style={{ color: 'var(--gray-500)', fontSize: '13px' }}>
                                  {pct}% of {p.item_name}
                                </td>
                                <td style={{ color: 'var(--gray-700)' }}>{v.total_qty}</td>
                                <td style={{ color: 'var(--gray-700)' }}>${v.total_revenue.toFixed(2)}</td>
                              </tr>
                            );
                          }
                        }
                        return rows;
                      })
                    )}
                  </tbody>
                  {report.products.length > 0 && (
                    <tfoot>
                      <tr>
                        <td colSpan={2}><strong>Total</strong></td>
                        <td><strong>{report.products.reduce((s, p) => s + p.total_qty, 0)}</strong></td>
                        <td><strong>${report.products.reduce((s, p) => s + p.total_revenue, 0).toFixed(2)}</strong></td>
                      </tr>
                    </tfoot>
                  )}
                </table>
              </div>
            </>
          )}
        </section>
      )}



      {tab === 'settings' && (
        <section className="admin-section">
          <h2>Settings</h2>
          {(() => {
            const settingsNav = [
              { id: 'general', label: 'General' },
              { id: 'ordering', label: 'Ordering' },
              { id: 'windows', label: 'Category windows' },
              ...(isSuperAdmin
                ? [
                  { id: 'payments', label: 'Payments' },
                  { id: 'stations', label: 'Counter stations' },
                  { id: 'print', label: 'Print agent' },
                  { id: 'admins', label: 'Admins' },
                  { id: 'audit', label: 'Audit' },
                ]
                : []),
              ...(currentAdmin ? [{ id: 'account', label: 'Password' }] : []),
            ];
            const activeSettingsGroup = settingsNav.some((group) => group.id === settingsGroup)
              ? settingsGroup
              : 'general';
            const activeLabel = settingsNav.find((group) => group.id === activeSettingsGroup)?.label;
            const openSettingsGroup = (id) => {
              setSettingsGroup(id);
              setSettingsShowDetail(true);
            };
            return (
          <div className={`settings-shell${settingsShowDetail ? ' has-detail' : ''}`}>
            <nav className="settings-nav card" aria-label="Settings">
              {settingsNav.map((group) => (
                <button
                  key={group.id}
                  type="button"
                  className={group.id === activeSettingsGroup ? 'is-active' : ''}
                  onClick={() => openSettingsGroup(group.id)}
                >
                  {group.label}
                </button>
              ))}
            </nav>
            <div className="settings-panel card">
              <button type="button" className="settings-back" onClick={() => setSettingsShowDetail(false)}>
                All settings
              </button>
              <h3 className="settings-panel-title">{activeLabel}</h3>

          {activeSettingsGroup === 'account' && currentAdmin && (
            <div className="settings-block">
              <p className="admin-hint">
                Signed in as {currentAdmin.email} ({currentAdmin.role}). Password must be 12+ characters with upper, lower, a number, and a symbol.
              </p>
              <form
                onSubmit={async (e) => {
                  e.preventDefault();
                  if (passwordForm.newPassword !== passwordForm.confirmPassword) {
                    alert('New passwords do not match');
                    return;
                  }
                  try {
                    await api.changeAdminPassword(
                      passwordForm.currentPassword,
                      passwordForm.newPassword
                    );
                    setPasswordForm({ currentPassword: '', newPassword: '', confirmPassword: '' });
                    alert('Password updated');
                  } catch (err) {
                    alert(err.message || 'Failed to change password');
                  }
                }}
              >
                <div className="form-group">
                  <label>Current password</label>
                  <input
                    type="password"
                    autoComplete="current-password"
                    required
                    value={passwordForm.currentPassword}
                    onChange={(e) => setPasswordForm((f) => ({ ...f, currentPassword: e.target.value }))}
                  />
                </div>
                <div className="form-row">
                  <div className="form-group">
                    <label>New password</label>
                    <input
                      type="password"
                      autoComplete="new-password"
                      required
                      value={passwordForm.newPassword}
                      onChange={(e) => setPasswordForm((f) => ({ ...f, newPassword: e.target.value }))}
                    />
                  </div>
                  <div className="form-group">
                    <label>Confirm new password</label>
                    <input
                      type="password"
                      autoComplete="new-password"
                      required
                      value={passwordForm.confirmPassword}
                      onChange={(e) => setPasswordForm((f) => ({ ...f, confirmPassword: e.target.value }))}
                    />
                  </div>
                </div>
                <button type="submit" className="btn btn-primary">Update password</button>
              </form>
            </div>
          )}

          {activeSettingsGroup === 'general' && (
          <form className="settings-block" onSubmit={handleSaveSettings}>
            <div className="form-group">
              <label>Church Name</label>
              <input name="church_name" defaultValue={settings.church_name || ''} />
            </div>
            <div className="form-row">
              <div className="form-group">
                <label>Order Prefix</label>
                <input name="order_prefix" defaultValue={settings.order_prefix || 'SB'} />
              </div>
              <div className="form-group">
                <label>Currency Symbol</label>
                <input name="currency_symbol" defaultValue={settings.currency_symbol || '$'} />
              </div>
            </div>
            {isSuperAdmin && (
              <div className="form-row">
                <div className="form-group">
                  <label>Admin PIN (break-glass only)</label>
                  <input name="admin_pin" type="text" inputMode="numeric" placeholder="Disabled once admin accounts exist" />
                </div>
                <div className="form-group">
                  <label>Cafe PIN</label>
                  <input name="cafe_pin" type="text" inputMode="numeric" placeholder="Enter new PIN to change" />
                </div>
                <div className="form-group">
                  <label>Kitchen PIN</label>
                  <input name="kitchen_pin" type="text" inputMode="numeric" placeholder="Enter new PIN to change" />
                </div>
                <div className="form-group">
                  <label>Counter POS PIN</label>
                  <input name="counter_pin" type="text" inputMode="numeric" placeholder="Enter new PIN to change" />
                </div>
              </div>
            )}

            <p className="admin-hint" style={{ marginTop: 16 }}>
              {isSuperAdmin ? (
                <>
                  Counter POS hardware (station name, receipt printer, terminal) is managed
                  per iPad under <strong>Counter stations</strong>.
                  EFTPOS provider and Tyro credentials are under <strong>Payments</strong>.
                </>
              ) : (
                <>
                  Counter stations, payments, PINs, and sticker printers are managed by a super admin.
                </>
              )}
            </p>

            <div className="form-actions">
              <button type="submit" className="btn btn-sm btn-primary">Save Settings</button>
            </div>
          </form>
          )}

          {activeSettingsGroup === 'print' && isSuperAdmin && (
          <div className="settings-block">
            <p className="admin-hint">
              One sticker is queued per cafe item. Copy the token into the cafe PC agent&apos;s <code>.env</code>.
            </p>
            <p className="admin-hint" style={{ marginTop: -8 }}>
              Backlog by printer —{' '}
              <strong>Cold/Cafe Food:</strong>{' '}
              {printAgent.stations?.cafe?.pending_jobs ?? printAgent.pending_jobs ?? 0} pending
              {printAgent.stations?.cafe?.failed_jobs > 0 && (
                <> · <span style={{ color: '#b91c1c' }}>{printAgent.stations.cafe.failed_jobs} failed</span></>
              )}
              {' · '}
              <strong>Hot drinks:</strong>{' '}
              {printAgent.stations?.cafe_hot?.pending_jobs ?? 0} pending
              {printAgent.stations?.cafe_hot?.failed_jobs > 0 && (
                <> · <span style={{ color: '#b91c1c' }}>{printAgent.stations.cafe_hot.failed_jobs} failed</span></>
              )}
              {printAgent.last_seen_ms ? (
                <>
                  {' · '}
                  Agent:{' '}
                  <strong style={{ color: printAgent.agent_ok ? '#065f46' : '#b91c1c' }}>
                    {printAgent.agent_ok ? 'checking in' : 'not seen recently'}
                  </strong>
                </>
              ) : null}
              .
            </p>
            <div className="form-group">
              <label>Print agent token</label>
              <input
                type="text"
                readOnly
                value={printAgent.print_agent_token || '(not set — generate a token)'}
                style={{ fontFamily: 'monospace', fontSize: 13 }}
                onFocus={(e) => e.target.select()}
              />
            </div>
            <div className="form-actions" style={{ gap: 8, flexWrap: 'wrap' }}>
              <button
                type="button"
                className="btn btn-sm btn-primary"
                disabled={printAgentBusy}
                onClick={async () => {
                  if (printAgent.print_agent_token
                    && !window.confirm('Rotate token? The cafe PC agent must be updated with the new token.')) {
                    return;
                  }
                  setPrintAgentBusy(true);
                  try {
                    const r = await api.rotatePrintAgentToken();
                    setPrintAgent((s) => ({ ...s, print_agent_token: r.print_agent_token }));
                    alert('Token saved. Copy it into the cafe PC agent .env now.');
                  } catch (err) {
                    alert(err.message || 'Failed to rotate token');
                  } finally {
                    setPrintAgentBusy(false);
                  }
                }}
              >
                {printAgent.print_agent_token ? 'Rotate token' : 'Generate token'}
              </button>
              <button
                type="button"
                className="btn btn-sm btn-outline"
                disabled={printAgentBusy}
                onClick={async () => {
                  setPrintAgentBusy(true);
                  try {
                    const r = await api.queueCafeTestSticker('cafe');
                    const status = await api.getPrintAgentStatus();
                    setPrintAgent(status);
                    alert(`Test sticker queued for the cold/cafe-food printer (job #${r.job_id}).`);
                  } catch (err) {
                    alert(err.message || 'Failed to queue test sticker');
                  } finally {
                    setPrintAgentBusy(false);
                  }
                }}
              >
                Test cold/Cafe Food printer
              </button>
              <button
                type="button"
                className="btn btn-sm btn-outline"
                disabled={printAgentBusy}
                onClick={async () => {
                  setPrintAgentBusy(true);
                  try {
                    const r = await api.queueCafeTestSticker('cafe_hot');
                    const status = await api.getPrintAgentStatus();
                    setPrintAgent(status);
                    alert(`Test sticker queued for the hot-drinks printer (job #${r.job_id}).`);
                  } catch (err) {
                    alert(err.message || 'Failed to queue test sticker');
                  } finally {
                    setPrintAgentBusy(false);
                  }
                }}
              >
                Test hot-drinks printer
              </button>
              <button
                type="button"
                className="btn btn-sm btn-outline"
                disabled={printAgentBusy}
                onClick={async () => {
                  setPrintAgentBusy(true);
                  try {
                    const status = await api.getPrintAgentStatus();
                    setPrintAgent(status);
                  } catch (err) {
                    alert(err.message || 'Refresh failed');
                  } finally {
                    setPrintAgentBusy(false);
                  }
                }}
              >
                Refresh queue
              </button>
              <button
                type="button"
                className="btn btn-sm btn-outline"
                disabled={
                  printAgentBusy
                  || !(
                    (printAgent.failed_jobs || 0) > 0
                    || (printAgent.stations?.cafe?.failed_jobs || 0) > 0
                    || (printAgent.stations?.cafe_hot?.failed_jobs || 0) > 0
                  )
                }
                title={
                  (printAgent.failed_jobs || 0) > 0
                    || (printAgent.stations?.cafe?.failed_jobs || 0) > 0
                    || (printAgent.stations?.cafe_hot?.failed_jobs || 0) > 0
                    ? 'Move failed sticker jobs back to pending so the agent prints them'
                    : 'No failed sticker jobs'
                }
                onClick={async () => {
                  setPrintAgentBusy(true);
                  try {
                    const preview = await api.requeueFailedPrintJobs({ dryRun: true });
                    if (!preview.would_requeue) {
                      alert('No failed cafe sticker jobs to re-queue.');
                      const status = await api.getPrintAgentStatus();
                      setPrintAgent(status);
                      return;
                    }
                    const perStation = (preview.by_station || [])
                      .map((r) => `  \u2022 ${r.station}: ${r.n}`)
                      .join('\n');
                    const ok = window.confirm(
                      `Re-queue ${preview.would_requeue} failed cafe sticker job(s) for printing?\n\n${perStation}\n\n` +
                        'Printers and the print agent should be on. Jobs will print shortly.'
                    );
                    if (!ok) return;
                    const result = await api.requeueFailedPrintJobs({ dryRun: false });
                    const status = await api.getPrintAgentStatus();
                    setPrintAgent(status);
                    alert(
                      `Re-queued ${result.requeued} failed job(s). ` +
                        'They should print while the agent and printers are online.'
                    );
                  } catch (err) {
                    alert(err.message || 'Re-queue failed');
                  } finally {
                    setPrintAgentBusy(false);
                  }
                }}
              >
                Print failed jobs
              </button>
              <button
                type="button"
                className="btn btn-sm btn-outline"
                disabled={printAgentBusy}
                onClick={async () => {
                  const raw = window.prompt(
                    'Cancel pending cafe stickers older than how many hours?\n' +
                      '(Use this after an outage so old jobs don\u2019t print when the agent comes back online. ' +
                      'Anything already printing or completed is untouched.)',
                    '24'
                  );
                  if (raw === null) return;
                  const hours = Number(raw);
                  if (!Number.isFinite(hours) || hours < 1) {
                    alert('Enter a whole number of hours, 1 or greater.');
                    return;
                  }
                  setPrintAgentBusy(true);
                  try {
                    const preview = await api.cleanupStalePrintJobs({
                      olderThanHours: hours,
                      dryRun: true,
                    });
                    if (!preview.would_cancel) {
                      alert(`No pending cafe stickers older than ${hours}h. Nothing to cancel.`);
                      return;
                    }
                    const perStation = (preview.by_station || [])
                      .map((r) => `  \u2022 ${r.station}: ${r.n}`)
                      .join('\n');
                    const ok = window.confirm(
                      `Cancel ${preview.would_cancel} pending cafe sticker job(s) older than ${hours}h?\n\n${perStation}\n\n` +
                        'Cancelled jobs will not print. This cannot be undone.'
                    );
                    if (!ok) return;
                    const result = await api.cleanupStalePrintJobs({
                      olderThanHours: hours,
                      dryRun: false,
                    });
                    const status = await api.getPrintAgentStatus();
                    setPrintAgent(status);
                    alert(`Cancelled ${result.cancelled} stale cafe sticker job(s).`);
                  } catch (err) {
                    alert(err.message || 'Cleanup failed');
                  } finally {
                    setPrintAgentBusy(false);
                  }
                }}
              >
                Cancel stale pending jobs…
              </button>
            </div>
          </div>
          )}

          {activeSettingsGroup === 'ordering' && (
          <div className="settings-block">
            <SettingsSwitchRow
              title="Sunday drink close"
              description="On Sunday, counter cold drinks close at 11:30am and hot drinks at 11:45am."
              checked={settings.sunday_drinks_cutoff === '1'}
              onChange={async (e) => {
                const val = e.target.checked ? '1' : '0';
                try {
                  await api.updateSettings({ sunday_drinks_cutoff: val });
                  setSettings((s) => ({ ...s, sunday_drinks_cutoff: val }));
                } catch (err) {
                  alert('Failed to update: ' + err.message);
                }
              }}
            />
            <SettingsSwitchRow
              title="Order for later"
              description="Cafe customers can choose a later pickup time at checkout."
              checked={settings.schedule_for_later === '1'}
              onChange={async (e) => {
                const val = e.target.checked ? '1' : '0';
                try {
                  await api.updateSettings({ schedule_for_later: val });
                  setSettings((s) => ({ ...s, schedule_for_later: val }));
                } catch (err) {
                  alert('Failed to update: ' + err.message);
                }
              }}
            />
            <SettingsSwitchRow
              title="Automatic scheduling"
              description="Turn orders on, drinks-only, or off at set times. Save after you change the slots."
              checked={schedule.enabled}
              onChange={(e) => setSchedule((s) => ({ ...s, enabled: e.target.checked }))}
            />

            {schedule.enabled && (
              <>
                {schedule.entries.map((entry, idx) => (
                  <div key={idx} className="schedule-entry card">
                    <div className="schedule-days">
                      {DAY_NAMES.map((day) => (
                        <button
                          key={day}
                          type="button"
                          className={`btn btn-sm ${entry.days.includes(day) ? 'btn-primary' : 'btn-outline'} schedule-day-btn`}
                          onClick={() => toggleScheduleDay(idx, day)}
                        >
                          {DAY_SHORT[day]}
                        </button>
                      ))}
                    </div>
                    <div className="schedule-times">
                      <div className="form-group">
                        <label>Start</label>
                        <input
                          type="time"
                          value={entry.start}
                          onChange={(e) => updateScheduleEntry(idx, 'start', e.target.value)}
                        />
                      </div>
                      <span className="schedule-to">to</span>
                      <div className="form-group">
                        <label>End</label>
                        <input
                          type="time"
                          value={entry.end}
                          onChange={(e) => updateScheduleEntry(idx, 'end', e.target.value)}
                        />
                      </div>
                      <div className="form-group">
                        <label>Menu</label>
                        <select
                          value={entry.menu === 'drinks' ? 'drinks' : 'full'}
                          onChange={(e) => updateScheduleEntry(idx, 'menu', e.target.value)}
                        >
                          <option value="full">Full</option>
                          <option value="drinks">Drinks</option>
                        </select>
                      </div>
                      <button
                        type="button"
                        className="btn btn-sm btn-danger"
                        onClick={() => removeScheduleEntry(idx)}
                      >
                        Remove
                      </button>
                      <label className="schedule-reminder">
                        <input
                          type="checkbox"
                          checked={entry.closingReminder !== false}
                          onChange={(e) => updateScheduleEntry(idx, 'closingReminder', e.target.checked)}
                        />
                        Send 30 min reminder
                      </label>
                    </div>
                    <p className="schedule-slot-hint">{orderScheduleHint.scheduleSlotSummary(entry)}</p>
                  </div>
                ))}
                <button type="button" className="btn btn-sm btn-outline" onClick={addScheduleEntry}>
                  + Add Time Slot
                </button>
              </>
            )}

            <div className="form-actions settings-save">
              <button type="button" className="btn btn-sm btn-primary" onClick={saveSchedule}>
                Save Schedule
              </button>
            </div>
          </div>
          )}

          {activeSettingsGroup === 'windows' && (
          <div className="settings-block">
            <p className="admin-hint">
              Limit when a cafe category can be ordered. Later pickup still uses the time the customer chose.
            </p>
            {categories.filter((c) => c.venue === 'cafe').map((cat) => {
              const cfg = { ...defaultWindow, ...(categoryWindows[cat.name] || {}) };
              return (
                <div key={cat.id} style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 10, flexWrap: 'wrap' }}>
                  <label style={{ display: 'flex', alignItems: 'center', gap: 6, minWidth: 160 }}>
                    <input
                      type="checkbox"
                      checked={!!cfg.enabled}
                      onChange={(e) => updateCategoryWindow(cat.name, 'enabled', e.target.checked)}
                    />
                    <span style={{ fontWeight: 600 }}>{cat.name}</span>
                  </label>
                  {cfg.enabled && (
                    <>
                      <label style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 14 }}>
                        From:
                        <input
                          type="time"
                          value={cfg.available_from || '10:00'}
                          onChange={(e) => updateCategoryWindow(cat.name, 'available_from', e.target.value)}
                          style={{ padding: '4px 8px', borderRadius: 'var(--radius-sm)', border: '1px solid var(--gray-300)' }}
                        />
                      </label>
                      <label style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 14 }}>
                        Until:
                        <input
                          type="time"
                          value={cfg.available_until || '15:00'}
                          onChange={(e) => updateCategoryWindow(cat.name, 'available_until', e.target.value)}
                          style={{ padding: '4px 8px', borderRadius: 'var(--radius-sm)', border: '1px solid var(--gray-300)' }}
                        />
                      </label>
                    </>
                  )}
                </div>
              );
            })}
            <div className="form-actions settings-save">
              <button type="button" className="btn btn-sm btn-primary" onClick={saveCategoryWindows}>
                Save Windows
              </button>
            </div>
          </div>
          )}
                {activeSettingsGroup === 'admins' && isSuperAdmin && (
          <div className="settings-block settings-embedded">
          <p className="admin-hint">
            Personal logins for the admin panel. Passwords must be at least 12 characters with upper, lower, a number, and a symbol.
            A Reporting User can open the dashboard and generate reports only.
            Cafe / kitchen / POS keep using shared PINs.
          </p>

          <div className="card" style={{ marginBottom: 16, padding: 16 }}>
            <h3 style={{ marginTop: 0 }}>Create admin</h3>
            <form
              className="form-row"
              style={{ flexWrap: 'wrap', gap: 12, alignItems: 'flex-end' }}
              onSubmit={async (e) => {
                e.preventDefault();
                setAdminBusy(true);
                try {
                  await api.createAdminAccount(adminForm);
                  setAdminForm({ name: '', email: '', password: '', role: 'admin' });
                  setAdminAccounts(await api.getAdminAccounts());
                  alert('Admin account created');
                } catch (err) {
                  alert(err.message || 'Failed to create admin');
                } finally {
                  setAdminBusy(false);
                }
              }}
            >
              <div className="form-group">
                <label>Name</label>
                <input
                  required
                  value={adminForm.name}
                  onChange={(e) => setAdminForm((f) => ({ ...f, name: e.target.value }))}
                />
              </div>
              <div className="form-group">
                <label>Email</label>
                <input
                  type="email"
                  required
                  value={adminForm.email}
                  onChange={(e) => setAdminForm((f) => ({ ...f, email: e.target.value }))}
                />
              </div>
              <div className="form-group">
                <label>Password</label>
                <input
                  type="password"
                  required
                  autoComplete="new-password"
                  value={adminForm.password}
                  onChange={(e) => setAdminForm((f) => ({ ...f, password: e.target.value }))}
                />
              </div>
              <div className="form-group">
                <label>Role</label>
                <select
                  value={adminForm.role}
                  onChange={(e) => setAdminForm((f) => ({ ...f, role: e.target.value }))}
                >
                  <option value="admin">admin</option>
                  <option value="reporter">Reporting User</option>
                  <option value="super_admin">super_admin</option>
                </select>
              </div>
              <button type="submit" className="btn btn-primary" disabled={adminBusy}>
                {adminBusy ? 'Saving…' : 'Create'}
              </button>
            </form>
          </div>

          {adminAccountsLoading ? (
            <p>Loading…</p>
          ) : (
            <div className="admin-table-wrap">
              <table className="admin-table">
                <thead>
                  <tr>
                    <th>Name</th>
                    <th>Email</th>
                    <th>Role</th>
                    <th>Status</th>
                    <th>Last login</th>
                    <th />
                  </tr>
                </thead>
                <tbody>
                  {adminAccounts.map((a) => (
                    <tr key={a.id}>
                      <td>{a.name}</td>
                      <td>{a.email}</td>
                      <td>
                        <select
                          className="admin-role-select"
                          value={a.role}
                          disabled={adminBusy}
                          title={
                            a.id === currentAdmin.id
                              ? 'Changing your own role takes effect on next sign-in'
                              : 'Change role'
                          }
                          onChange={async (e) => {
                            const role = e.target.value;
                            if (role === a.role) return;
                            if (
                              a.id === currentAdmin.id &&
                              a.role === 'super_admin' &&
                              role !== 'super_admin' &&
                              !window.confirm(
                                'Demote your own account from super_admin? You will lose Admins/Audit access after you sign in again.'
                              )
                            ) {
                              e.target.value = a.role;
                              return;
                            }
                            setAdminBusy(true);
                            try {
                              await api.updateAdminAccount(a.id, { role });
                              setAdminAccounts(await api.getAdminAccounts());
                              if (a.id === currentAdmin.id) {
                                setCurrentAdmin((prev) => (prev ? { ...prev, role } : prev));
                              }
                            } catch (err) {
                              alert(err.message);
                              e.target.value = a.role;
                            } finally {
                              setAdminBusy(false);
                            }
                          }}
                        >
                          <option value="admin">admin</option>
                          <option value="reporter">Reporting User</option>
                          <option value="super_admin">super_admin</option>
                        </select>
                      </td>
                      <td>
                        <label className="settings-switch" title={a.id === currentAdmin.id ? 'You cannot disable your own account' : (a.is_active ? 'Active' : 'Disabled')}>
                          <input
                            type="checkbox"
                            checked={!!a.is_active}
                            disabled={adminBusy || a.id === currentAdmin.id}
                            aria-label={a.is_active ? `Disable ${a.name}` : `Enable ${a.name}`}
                            onChange={async (e) => {
                              const next = e.target.checked;
                              setAdminBusy(true);
                              try {
                                await api.updateAdminAccount(a.id, { is_active: next });
                                setAdminAccounts(await api.getAdminAccounts());
                              } catch (err) {
                                alert(err.message);
                              } finally {
                                setAdminBusy(false);
                              }
                            }}
                          />
                          <span className="settings-switch-track" aria-hidden="true" />
                        </label>
                      </td>
                      <td>{a.last_login_at ? fmtAU(parseDbDateTime(a.last_login_at)) : '—'}</td>
                      <td style={{ whiteSpace: 'nowrap' }}>
                        <button
                          type="button"
                          className="btn btn-sm btn-outline"
                          disabled={adminBusy}
                          onClick={async () => {
                            const password = window.prompt(
                              `New password for ${a.email} (12+ chars, upper, lower, number, symbol):`
                            );
                            if (!password) return;
                            setAdminBusy(true);
                            try {
                              await api.updateAdminAccount(a.id, { password });
                              alert('Password updated');
                            } catch (err) {
                              alert(err.message);
                            } finally {
                              setAdminBusy(false);
                            }
                          }}
                        >
                          Reset password
                        </button>
                        {' '}
                        <button
                          type="button"
                          className="btn btn-sm btn-danger"
                          disabled={adminBusy || a.id === currentAdmin.id}
                          onClick={async () => {
                            if (!window.confirm(`Delete admin ${a.email}?`)) return;
                            setAdminBusy(true);
                            try {
                              await api.deleteAdminAccount(a.id);
                              setAdminAccounts(await api.getAdminAccounts());
                            } catch (err) {
                              alert(err.message);
                            } finally {
                              setAdminBusy(false);
                            }
                          }}
                        >
                          Delete
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
          </div>
          )}
                {activeSettingsGroup === 'audit' && isSuperAdmin && (
          <div className="settings-block settings-embedded">
          <p className="admin-hint">
            Successful admin changes only. Passwords and PINs are redacted. Visible to super_admin only.
          </p>
          {auditLoading ? (
            <p>Loading…</p>
          ) : auditRows.length === 0 ? (
            <p className="admin-hint">No audit entries yet. Changes made after this deploy will appear here.</p>
          ) : (
            <>
              <div className="admin-table-wrap">
                <table className="admin-table">
                  <thead>
                    <tr>
                      <th>When</th>
                      <th>Who</th>
                      <th>Action</th>
                      <th>Path</th>
                      <th>IP</th>
                      <th />
                    </tr>
                  </thead>
                  <tbody>
                    {auditRows.map((row) => (
                      <tr key={row.id}>
                        <td>{row.created_at ? fmtAU(parseDbDateTime(row.created_at)) : '—'}</td>
                        <td>
                          <div>{row.actor_email || '—'}</div>
                          <div style={{ fontSize: 12, color: 'var(--gray-500)' }}>
                            {row.actor_role || ''}
                          </div>
                        </td>
                        <td>
                          <div>{row.details?.summary || <code>{row.action}</code>}</div>
                          {row.details?.summary && (
                            <div style={{ fontSize: 12, color: 'var(--gray-500)' }}>
                              <code>{row.action}</code>
                            </div>
                          )}
                          {row.entity_type && (
                            <div style={{ fontSize: 12, color: 'var(--gray-500)' }}>
                              {row.entity_type}{row.entity_id ? ` #${row.entity_id}` : ''}
                            </div>
                          )}
                        </td>
                        <td style={{ maxWidth: 220, overflow: 'hidden', textOverflow: 'ellipsis' }}>
                          {row.method} {row.path}
                        </td>
                        <td>{row.ip || '—'}</td>
                        <td>
                          <button
                            type="button"
                            className="btn btn-sm btn-outline"
                            onClick={() => setAuditDetail(row)}
                          >
                            Details
                          </button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
              <div style={{ display: 'flex', gap: 8, alignItems: 'center', marginTop: 12 }}>
                <button
                  type="button"
                  className="btn btn-sm btn-outline"
                  disabled={auditOffset <= 0 || auditLoading}
                  onClick={() => setAuditOffset((o) => Math.max(0, o - AUDIT_PAGE))}
                >
                  Previous
                </button>
                <span style={{ fontSize: 13, color: 'var(--gray-600)' }}>
                  {auditOffset + 1}–{Math.min(auditOffset + auditRows.length, auditTotal)} of {auditTotal}
                </span>
                <button
                  type="button"
                  className="btn btn-sm btn-outline"
                  disabled={auditOffset + AUDIT_PAGE >= auditTotal || auditLoading}
                  onClick={() => setAuditOffset((o) => o + AUDIT_PAGE)}
                >
                  Next
                </button>
              </div>
            </>
          )}

          {auditDetail && (
            <div className="admin-modal-overlay" onClick={() => setAuditDetail(null)}>
              <div className="card" onClick={(e) => e.stopPropagation()} style={{ maxWidth: 640, padding: 20 }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                  <h3 style={{ margin: 0 }}>Audit #{auditDetail.id}</h3>
                  <button type="button" className="btn btn-sm btn-outline" onClick={() => setAuditDetail(null)}>
                    Close
                  </button>
                </div>
                <p style={{ margin: '12px 0 8px', fontSize: 14 }}>
                  <strong>{auditDetail.actor_email || '—'}</strong>
                  {' · '}
                  {auditDetail.action}
                  {' · '}
                  {auditDetail.created_at ? fmtAU(parseDbDateTime(auditDetail.created_at)) : '—'}
                </p>
                {auditDetail.details?.summary && (
                  <p style={{ margin: '0 0 12px', fontSize: 15, fontWeight: 600 }}>
                    {auditDetail.details.summary}
                  </p>
                )}
                <pre
                  style={{
                    margin: 0,
                    padding: 12,
                    background: 'var(--gray-100, #f3f4f6)',
                    borderRadius: 8,
                    fontSize: 12,
                    overflow: 'auto',
                    maxHeight: '50vh',
                  }}
                >
                  {JSON.stringify(auditDetail.details, null, 2)}
                </pre>
              </div>
            </div>
          )}
          </div>
          )}
                {activeSettingsGroup === 'stations' && isSuperAdmin && (
          <div className="settings-block settings-embedded">
          <p className="admin-hint">
            One row per physical till (iPad + Tyro terminal + docket printer).
            Only one iPad can hold a station at a time. Release it here if an
            iPad was put away without using Change station.
          </p>

          <div className="card" style={{ marginBottom: 16 }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 12 }}>
              <h3 style={{ margin: 0 }}>Stations</h3>
              <button
                type="button"
                className="btn btn-sm btn-primary"
                onClick={() => setEditingStation({})}
              >
                + Add station
              </button>
            </div>

            {posStationsLoading ? (
              <p className="admin-hint">Loading…</p>
            ) : posStations.length === 0 ? (
              <p className="admin-hint">
                No stations yet. Add one — the POS won't let staff sign in without at least one active station.
              </p>
            ) : (
              <div className="admin-table-wrap">
                <table className="admin-table">
                  <thead>
                    <tr>
                      <th>Name</th>
                      <th>Station key</th>
                      <th>Tyro TID</th>
                      <th>Printer</th>
                      <th>Active</th>
                      <th>In use</th>
                      <th>Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {posStations.map((s) => (
                      <tr key={s.id}>
                        <td><strong>{s.name}</strong></td>
                        <td><code style={{ fontSize: 12 }}>{s.station_key}</code></td>
                        <td>{s.tyro_terminal_id || <em style={{ color: 'var(--gray-500)' }}>use default</em>}</td>
                        <td>
                          {s.printer_ip
                            ? `${s.printer_ip}:${s.printer_port}`
                            : <em style={{ color: 'var(--gray-500)' }}>not set</em>}
                        </td>
                        <td>
                          <label className="settings-switch" title={s.active ? 'Active' : 'Inactive'}>
                            <input
                              type="checkbox"
                              checked={!!s.active}
                              aria-label={s.active ? `Deactivate ${s.name}` : `Activate ${s.name}`}
                              onChange={(e) => handleStationActive(s, e.target.checked)}
                            />
                            <span className="settings-switch-track" aria-hidden="true" />
                          </label>
                        </td>
                        <td>{s.claimed ? 'Yes — an iPad holds it' : 'Free'}</td>
                        <td>
                          <button
                            type="button"
                            className="btn btn-xs btn-outline"
                            onClick={() => setEditingStation(s)}
                          >
                            Edit
                          </button>
                          {s.claimed && (
                            <button
                              type="button"
                              className="btn btn-xs btn-outline"
                              style={{ marginLeft: 6 }}
                              onClick={async () => {
                                if (!window.confirm(`Release "${s.name}" so another iPad can pick it?`)) return;
                                try {
                                  await api.adminReleasePosStation(s.id);
                                  await refreshPosStations();
                                } catch (err) {
                                  alert(err.message || 'Failed to release station');
                                }
                              }}
                            >
                              Release
                            </button>
                          )}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>

          {editingStation && (
            <div className="card" style={{ marginBottom: 16 }}>
              <h3 style={{ marginTop: 0 }}>
                {editingStation.id ? `Edit "${editingStation.name}"` : 'New station'}
              </h3>
              <form className="admin-form" onSubmit={handleStationSubmit}>
                <div className="form-row">
                  <div className="form-group">
                    <label>Name *</label>
                    <input
                      name="name"
                      defaultValue={editingStation.name || ''}
                      placeholder="Station 1"
                      required
                    />
                  </div>
                  <div className="form-group">
                    <label>Station key</label>
                    <input
                      name="station_key"
                      defaultValue={editingStation.station_key || ''}
                      placeholder="auto from name"
                    />
                    <small className="admin-hint">URL-safe slug. Leave blank to auto-generate.</small>
                  </div>
                </div>
                <div className="form-row">
                  <div className="form-group">
                    <label>Tyro Terminal ID (TID)</label>
                    <input
                      name="tyro_terminal_id"
                      defaultValue={editingStation.tyro_terminal_id || ''}
                      placeholder="Leave blank to use the default TID from Payments"
                    />
                  </div>
                </div>
                <div className="form-row">
                  <div className="form-group">
                    <label>Printer IP</label>
                    <input
                      name="printer_ip"
                      defaultValue={editingStation.printer_ip || ''}
                      placeholder="192.168.0.50"
                    />
                  </div>
                  <div className="form-group">
                    <label>Printer Port</label>
                    <input
                      name="printer_port"
                      defaultValue={editingStation.printer_port || '9100'}
                      placeholder="9100"
                      inputMode="numeric"
                    />
                  </div>
                </div>
                {editingStation.id && (
                  <div className="form-row">
                    <div className="form-group">
                      <label>
                        <input
                          type="checkbox"
                          name="active"
                          defaultChecked={editingStation.active !== false}
                        />
                        {' '}Active
                      </label>
                    </div>
                  </div>
                )}
                <div className="form-actions">
                  <button
                    type="button"
                    className="btn btn-sm btn-outline"
                    onClick={() => setEditingStation(null)}
                    disabled={stationBusy}
                  >
                    Cancel
                  </button>
                  <button
                    type="submit"
                    className="btn btn-sm btn-primary"
                    disabled={stationBusy}
                  >
                    {stationBusy ? 'Saving…' : (editingStation.id ? 'Save changes' : 'Create station')}
                  </button>
                </div>
              </form>
            </div>
          )}
          </div>
          )}
                {activeSettingsGroup === 'payments' && isSuperAdmin && (
          <div className="settings-block settings-embedded">
          <p className="admin-hint">
            EFTPOS provider + Tyro Pay Instore credentials. Per-station
            terminal IDs are set under <strong>Counter stations</strong>; the
            default TID here is only used as a fallback when a station has
            none of its own.
          </p>

          <form className="admin-form card" onSubmit={handleSaveSettings}>
            <div className="form-row">
              <div className="form-group">
                <label>EFTPOS Provider</label>
                <select
                  name="eftpos_provider"
                  value={settings.eftpos_provider || 'manual'}
                  onChange={(e) => setSettings((s) => ({ ...s, eftpos_provider: e.target.value }))}
                >
                  <option value="manual">Manual (staff types on DX8000)</option>
                  <option value="tyro_pay_instore">Tyro Pay Instore (integrated via HTTPS) — requires Connect credentials</option>
                  <option value="tyro">Tyro iClient (browser SDK — deprecated for iPad)</option>
                </select>
                <small className="admin-hint">
                  Controls what the "Tyro EFTPOS" button on the POS does.
                  "Manual" records the outcome after the operator taps
                  Approved/Declined on the DX8000; "Pay Instore" sends the
                  sale straight to the terminal via HTTPS.
                </small>
              </div>
            </div>

            <h3 style={{ marginTop: 24, marginBottom: 8 }}>Tyro Pay Instore</h3>
            <p className="admin-hint">
              Server-side integration with the Tyro Connect Pay Instore API.
              Credentials themselves live in environment variables
              (<code>TYRO_CLIENT_ID</code>, <code>TYRO_CLIENT_SECRET</code>,
              <code> TYRO_WEBHOOK_SIGNING_KEY</code>) — see{' '}
              <code>docs/tyro/README.md</code>. Only operational config is
              editable here.
            </p>
            <div className="form-row">
              <div className="form-group">
                <label>Tyro Merchant ID (MID)</label>
                <input
                  name="tyro_merchant_id"
                  defaultValue={settings.tyro_merchant_id || ''}
                  placeholder="e.g. 12345678"
                />
              </div>
              <div className="form-group">
                <label>Default Terminal ID (TID)</label>
                <input
                  name="tyro_default_terminal_id"
                  defaultValue={settings.tyro_default_terminal_id || ''}
                  placeholder="Fallback TID when a station has none set"
                />
              </div>
              <div className="form-group">
                <label>Tyro API Base URL</label>
                <input
                  name="tyro_base_url"
                  defaultValue={settings.tyro_base_url || 'https://api.connect.tyro.com'}
                  placeholder="https://api.connect.tyro.com"
                />
              </div>
            </div>

            <div className="form-row">
              <div className="form-group">
                <label>Integrated receipts</label>
                <select
                  name="tyro_integrated_receipt"
                  defaultValue={settings.tyro_integrated_receipt === '0' ? '0' : '1'}
                >
                  <option value="1">On — Tyro returns receipt text to POS (cert default)</option>
                  <option value="0">Off — terminal handles receipts</option>
                </select>
                <small className="admin-hint">
                  When on, Create Pay Request sets <code>options.integratedReceipt = true</code>.
                  In production, receipts are stored and printed only when staff tap Print on the POS —
                  never automatically after approval (kitchen dockets still print as usual).
                </small>
              </div>
              <div className="form-group">
                <label>Receipt width (chars)</label>
                <input
                  name="tyro_receipt_width"
                  defaultValue={settings.tyro_receipt_width || '42'}
                  placeholder="42"
                  inputMode="numeric"
                />
              </div>
            </div>

            <div className="form-row">
              <div className="form-group">
                <label>Save card on checkout (Pay Online)</label>
                <select
                  name="tyro_save_card"
                  defaultValue={settings.tyro_save_card === '1' ? '1' : '0'}
                >
                  <option value="0">Off — customers enter their card each time</option>
                  <option value="1">On — logged-in customers can save a Tyro-tokenised card</option>
                </select>
                <small className="admin-hint">
                  Off by default. When on, signed-in customers get a &quot;Save this card&quot; checkbox
                  (on by default) so Tyro tokenises the first Pay Request — changing it after typing
                  reloads the card fields. We store Tyro tokens only (last 4 + expiry) — never the card number.
                  Apple Pay / Google Pay cannot be saved. Do a $1 save and $1 reuse test before leaving this on.
                </small>
                {typeof stats?.saved_card_people === 'number' && (
                  <small className="admin-hint" style={{ display: 'block', marginTop: 8 }}>
                    {stats.saved_card_people === 0
                      ? 'No one has saved a card yet.'
                      : `${stats.saved_card_people} ${stats.saved_card_people === 1 ? 'person has' : 'people have'} a saved card${
                          stats.saved_card_count !== stats.saved_card_people
                            ? ` (${stats.saved_card_count} cards)`
                            : ''
                        }.`}
                  </small>
                )}
              </div>
            </div>

            <div className="form-actions">
              <button type="submit" className="btn btn-sm btn-primary">Save Payments</button>
            </div>
          </form>
          </div>
          )}
            </div>
          </div>
            );
          })()}
        </section>
      )}

      <AdminConfirmModal
        open={!!ordersDialog}
        busy={!!ordersDialog?.busy}
        title={
          ordersDialog?.mode === 'from-on'
            ? 'App Orders are ON'
            : ordersDialog?.mode === 'from-drinks'
              ? 'App Orders are drinks only'
              : ordersDialog?.mode === 'from-off'
                ? 'Start App Orders?'
                : ordersDialog?.title || 'App Orders'
        }
        onClose={ordersDialog?.busy ? undefined : () => setOrdersDialog(null)}
        actions={
          ordersDialog?.mode === 'from-on'
            ? [
                {
                  key: 'cancel',
                  label: 'Cancel',
                  variant: 'outline',
                  onClick: () => setOrdersDialog(null),
                },
                {
                  key: 'drinks',
                  label: ordersDialog.busy ? 'Updating…' : 'Drinks only',
                  variant: 'outline',
                  onClick: applyOrdersDrinks,
                },
                {
                  key: 'stop',
                  label: ordersDialog.busy ? 'Stopping…' : 'Stop App Orders',
                  variant: 'danger',
                  onClick: applyOrdersOff,
                },
              ]
            : ordersDialog?.mode === 'from-drinks'
              ? [
                  {
                    key: 'cancel',
                    label: 'Cancel',
                    variant: 'outline',
                    onClick: () => setOrdersDialog(null),
                  },
                  {
                    key: 'full',
                    label: ordersDialog.busy ? 'Updating…' : 'Full menu ON',
                    variant: 'outline',
                    onClick: () => applyOrdersOn({ notify: false }),
                  },
                  {
                    key: 'stop',
                    label: ordersDialog.busy ? 'Stopping…' : 'Stop App Orders',
                    variant: 'danger',
                    onClick: applyOrdersOff,
                  },
                ]
              : ordersDialog?.mode === 'from-off'
              ? [
                  {
                    key: 'cancel',
                    label: 'Cancel',
                    variant: 'outline',
                    onClick: () => setOrdersDialog(null),
                  },
                  {
                    key: 'drinks',
                    label: ordersDialog.busy ? 'Updating…' : 'Drinks only',
                    variant: 'outline',
                    onClick: applyOrdersDrinks,
                  },
                  {
                    key: 'on-only',
                    label: ordersDialog.busy ? 'Updating…' : 'Orders ON only',
                    variant: 'outline',
                    onClick: () => applyOrdersOn({ notify: false }),
                  },
                  {
                    key: 'notify',
                    label: ordersDialog.busy ? 'Sending…' : 'Notify everyone',
                    variant: 'primary',
                    onClick: () => applyOrdersOn({ notify: true }),
                  },
                ]
              : [
                  {
                    key: 'ok',
                    label: 'OK',
                    variant: 'primary',
                    onClick: () => setOrdersDialog(null),
                  },
                ]
        }
      >
        {ordersDialog?.mode === 'from-on'
          ? 'Full app menu is on. Switch to drinks only, or stop the app.\n\nCounter POS still sells everything.'
          : ordersDialog?.mode === 'from-drinks'
            ? 'The app is drinks only. Switch back to the full menu, or stop the app.\n\nKitchen and cafe food stay on the POS.'
            : ordersDialog?.mode === 'from-off'
              ? 'Start accepting orders from the mobile app?\n\nDrinks only hides kitchen and cafe food on the app. Notify everyone is for a full-menu open.'
              : ordersDialog?.body || ''}
      </AdminConfirmModal>
    </main>
  );
}
